import { useState } from "react";
import { createRoute, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Activity, BadgePercent, Banknote, Bookmark, ClipboardCheck, ClipboardList, Coins, Images, KeyRound, LayoutDashboard, LayoutGrid, LayoutTemplate, Link2, Mail, Megaphone, Network, Package, Palette, Plus, Receipt, RotateCcw, Scale, Search, Settings2, ShieldCheck, Sofa, Timer, Truck, Users, Wallet } from "lucide-react";

import { rootRoute } from "./root.tsx";
import { Badge, Desk, ProductStatusBadge, Table, Td, Tr } from "../components/desk.tsx";
import { cn } from "@/lib/utils";
import { BrandsTab, CategoriesTab, FreightTab } from "./admin-taxonomy.tsx";
import { OverviewTab } from "./admin-overview.tsx";
import {
  AllProductsTab,
  AnnouncementTab,
  HealthTab,
  PriceRulesTab,
  UsersTab,
  ApproveWithPriceDialog,
  AppearanceTab,
} from "./admin-ops.tsx";
import { AdminAccountsTab, AdminRolesTab } from "./admin-rbac.tsx";
import { ReasonDialog } from "../components/reason-dialog.tsx";
import { PickDialog } from "../components/pick-dialog.tsx";
import { Button, Empty, ErrorBlock, Img, Input, OrderStatus, Price, Spinner, currencySymbol, fen } from "../components/ui.tsx";
import { api } from "../api/client.ts";
import { toCents } from "../api/org-client.ts";
import { CaseEditor, type AdminCase } from "./admin-case-editor.tsx";
import { useRequireLogin } from "../lib/require-login.ts";
import { LoginPageTab } from "./admin-login-page.tsx";
import { SpacesTab } from "./admin-spaces.tsx";
import { useMe } from "../api/queries.ts";

/**
 * 平台后台（桌面版）。
 *
 * 🔴 这里能做的每一件事都会影响别人的钱：审商品、清退商家、审提现、改抽成比例。
 *    所以危险操作一律要填理由，且理由会原样进留痕表。
 */

interface PendingProduct {
  id: number;
  title: string;
  status: string;
  supplierOrgId: number;
  minSupplyPrice: number;
  createdAt: string;
  imageUrls: string[];
  /** 🔴 显示名字不显示 `#id` —— 审核员要判断"这家靠不靠谱"，编号说明不了任何事。 */
  supplierName: string;
  skuCount: number;
  unit: string;
  /** ⚠️ 只有一个在售规格时才有值 —— 列表里那个平台价输入框就是填给它的。 */
  onlySkuId: number | null;
}
interface OrgRow {
  id: number;
  parentId: number | null;
  name: string;
  type: string;
  status: string;
  depth: number;
  levelKey: string;
  inviteCode: string;
}
/**
 * 周期费订阅与保证金。
 *
 * 🔴 这两样是**六种收费方式里合规风险最高的两种** ——
 *    向零门槛注册的分销商收入门费会踩传销认定的红线，
 *    所以后台这一页要把"这条策略对谁开放"摆在最显眼的地方。
 */
interface FeeOption {
  id: number;
  kind: string;
  period: string | null;
  amount: number;
  note: string;
}
interface SubscriptionRow {
  id: number;
  period: string;
  amount: number;
  status: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  nextBillAt: string | null;
}
interface DepositRow {
  id: number;
  amount: number;
  status: string;
  paidAt: string | null;
  refundedAt: string | null;
  forfeitReason: string;
}
interface JobHealth {
  name: string;
  label: string;
  intervalMs: number;
  lastAt: string | null;
  lastStatus: string | null;
  lastDurationMs: number | null;
  lastResult: Record<string, unknown> | null;
  lastError: string;
  /** 超过两个周期没跑过 —— 服务端算好的，前端不重复判断。 */
  overdue: boolean;
}
interface NotifyRow {
  id: number;
  event: string;
  orderId: number;
  toEmail: string;
  status: string;
  attempts: number;
  lastError: string;
  createdAt: string;
}
interface JobRunRow {
  id: number;
  name: string;
  status: string;
  startedAt: string;
  durationMs: number;
  result: Record<string, unknown>;
  error: string;
}
interface WithdrawalRow {
  id: number;
  withdrawalNo: string;
  orgId: number;
  amount: number;
  fee: number;
  actualAmount: number;
  status: string;
  payeeSnapshot: Record<string, string>;
  createdAt: string;
}
interface SettingRecord {
  key: string;
  value: Record<string, unknown>;
  updatedBy: string | null;
  updatedAt: string;
  isDefault: boolean;
}

const TYPE_LABEL: Record<string, string> = {
  platform: "平台",
  supplier: "供应商",
  dealer: "经销商",
  distributor: "分销商",
};

const GROUP_LABEL: Record<string, string> = {
  site: "站点信息",
  binding: "归属规则",
  pricing: "价格规则",
  settlement: "结算规则",
  commission: "分佣规则",
  org: "组织规则",
  fulfillment: "履约规则",
  payment: "收款",
  tax: "个税代扣",
  appearance: "外观",
};

/**
 * 配置项的中文名。
 *
 * 🔴 原先页面上直接印英文 key（collectMode、minWithdrawal…）——
 *    老板看不懂就不敢动，等于这些开关不存在。
 * ⚠️ 没列到的 key 原样显示英文，不会不显示。
 */
const FIELD_LABEL: Record<string, string> = {
  name: "站点名称", icp: "备案号", contactPhone: "客服电话",
  baseUrl: "对外网址（分享链接用它拼，留空=用当前域名）",
  theme: "配色", script: "字体", currency: "货币",
  conflictPolicy: "归属冲突", expiryDays: "归属有效期（天）", breakPolicy: "上级清退后",
  defaultMarkupRate: "兜底加价率", belowCostPolicy: "亏本销售", priceChangeBufferHours: "调价缓冲（小时）",
  afterSaleDays: "售后维权期（天）", minWithdrawal: "最低提现额（分）", withdrawalFeeRate: "提现手续费率",
  levels: "分佣层数", minRetainRate: "被抽方保底比例", directOnly: "只对直接下级抽成",
  maxDepth: "关系链深度上限", distributorCanRecruit: "分销商可发展下级",
  auditDealer: "经销商入驻审资质", auditDistributor: "分销商入驻审资质",
  shipBy: "谁发货", afterSaleBy: "谁管售后",
  collectMode: "钱怎么收", currentGateway: "收款通道", orderTimeoutMinutes: "订单有效期（分钟）",
  offlineTimeoutHours: "线下收款有效期（小时）", splitEnabled: "超限额拆单分付",
  splitPerPieceMax: "每笔上限（分）", splitMaxPieces: "封顶笔数", qrcodeUrl: "收款码图片地址",
  method: "代扣口径", threshold: "免扣线（分）", flatRate: "核定比例",
};

/** 枚举型配置的可选值。⚠️ 用下拉而不是文本框 —— 手打错一个字母就是整站行为异常。 */
const FIELD_OPTIONS: Record<string, [string, string][]> = {
  /** ⚠️ 外观在「外观」那一页有色卡可视化地选，这里只是兜底能改。 */
  theme: [["terracotta", "赤陶（默认）"], ["ocean", "海蓝"], ["forest", "松绿"], ["graphite", "石墨"], ["plum", "梅红"]],
  script: [["hans", "简体中文"], ["hant", "繁體中文"]],
  currency: [["CNY", "人民币 ¥"], ["TWD", "新台币 NT$（只换符号，不折算金额）"]],
  conflictPolicy: [["first_win", "先绑定者优先（防抢客）"], ["last_win", "后绑定者覆盖"]],
  breakPolicy: [["attach_parent", "下级上挂一级"], ["detach", "解绑"], ["manual", "人工处理"]],
  belowCostPolicy: [["allow", "允许"], ["warn", "警告但放行"], ["block", "禁止"]],
  shipBy: [["supplier_direct", "供应商直发"], ["seller_stock", "卖家自己备货发货"]],
  afterSaleBy: [["seller", "卖家负责，平台可介入"], ["platform", "平台统一处理"]],
  collectMode: [
    ["offline_direct", "线下收款：只建订单，钱不经平台"],
    ["platform_gateway", "平台代收：钱经平台，再分账入余额"],
  ],
  currentGateway: [["zhongzhuan", "中转站（支付宝/微信）"], ["qrcode", "静态收款码，人工核账"]],
  method: [["none", "不代扣"], ["labor", "劳务报酬预扣预缴"], ["flat", "比例核定"]],
};

/**
 * 订单查询的状态筛选。
 *
 * 🔴 **付款和履约是两个正交的状态**（见 `enums.ts` 上的说明），
 *    所以一个筛选项可能要同时给两维定值。
 * ⚠️ 「有退款」按 payment 筛，退款是钱的事，跟发没发货无关。
 */
const ORDER_FILTERS: { key: string; label: string; payment?: string; fulfillment?: string }[] = [
  { key: "all", label: "全部" },
  { key: "unpaid", label: "待付款", payment: "pending" },
  { key: "toship", label: "待发货", payment: "paid", fulfillment: "pending" },
  { key: "shipped", label: "已发货", fulfillment: "shipped" },
  { key: "delivered", label: "已收货", fulfillment: "delivered" },
  { key: "completed", label: "已完成", fulfillment: "completed" },
  { key: "refunded", label: "有退款", payment: "partial_refunded" },
  { key: "cancelled", label: "已取消", fulfillment: "cancelled" },
];

/**
 * 一组配置的编辑表单。
 *
 * 🔴 **配置中心以前是只读的** —— 保存接口早就齐了，页面上却只有一张表，
 *    老板一个开关都改不了。有一个改不了的开关比没有开关更糟：
 *    看得见就会以为已经生效了。
 *
 * ⚠️ **带乐观锁**：把打开表单时的 updatedAt 一起提交，
 *    两个人同时编辑同一组时后存的会被挡下，而不是静默覆盖前一个人的改动。
 */
function SettingsForm({
  group,
  rec,
  onDone,
}: {
  group: string;
  rec: SettingRecord;
  onDone: () => void;
}) {
  const [draft, setDraft] = useState<Record<string, unknown>>(rec.value);

  const save = useMutation({
    mutationFn: () =>
      api.put(`/admin/settings/${group}`, {
        value: draft,
        /** ⚠️ isDefault 表示库里还没有这一行，这时没有乐观锁可带。 */
        expectedUpdatedAt: rec.isDefault ? null : rec.updatedAt,
      }),
    onSuccess: onDone,
  });

  const set = (k: string, v: unknown) => setDraft((d) => ({ ...d, [k]: v }));

  return (
    <div className="space-y-2.5">
      {Object.entries(rec.value).map(([k, original]) => {
        const v = draft[k];
        const options = FIELD_OPTIONS[k];

        return (
          <label key={k} className="block">
            <span className="mb-1 block text-xs text-ink-500">{FIELD_LABEL[k] ?? k}</span>

            {options ? (
              <select
                className="w-full rounded border border-neutral-300 px-2 py-1.5 text-xs"
                value={String(v ?? "")}
                onChange={(e) => set(k, e.target.value)}
              >
                {options.map(([val, text]) => (
                  <option key={val} value={val}>
                    {text}
                  </option>
                ))}
              </select>
            ) : typeof original === "boolean" ? (
              <select
                className="w-full rounded border border-neutral-300 px-2 py-1.5 text-xs"
                value={v ? "1" : "0"}
                onChange={(e) => set(k, e.target.value === "1")}
              >
                <option value="1">是</option>
                <option value="0">否</option>
              </select>
            ) : (
              <input
                className="w-full rounded border border-neutral-300 px-2 py-1.5 text-xs"
                value={v === null || v === undefined ? "" : String(v)}
                placeholder={original === null ? "留空 = 不限" : ""}
                onChange={(e) => {
                  const raw = e.target.value;
                  /**
                   * ⚠️ 按**原值的类型**决定怎么解析，不看用户输入长什么样：
                   *    数字字段留空要变回 null（"不限"），不是变成 0 ——
                   *    「深度 0」和「不限」是完全相反的两件事。
                   */
                  if (typeof original === "number") {
                    set(k, raw.trim() === "" ? null : Number(raw));
                  } else {
                    set(k, raw === "" && original === null ? null : raw);
                  }
                }}
              />
            )}
          </label>
        );
      })}

      <div className="flex gap-2 pt-1">
        <button className="rounded border border-neutral-300 px-3 py-1.5 text-xs" onClick={onDone}>
          取消
        </button>
        <button
          className="rounded bg-brand-600 px-3 py-1.5 text-xs text-white disabled:opacity-50"
          disabled={save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? "保存中…" : "保存"}
        </button>
      </div>
      {save.error && <ErrorBlock error={save.error} />}
    </div>
  );
}

function Admin() {
  const search = useSearch({ from: "/admin" }) as { tab?: string };
  /**
   * 🔴 **默认落到概览，不是「商品审核」。**
   *    以前打开后台第一眼是一张空表 —— 运营想知道的是"今天有什么等着我处理"。
   */
  const tab = search.tab ?? "overview";
  const { data: me, isPending: mePending } = useMe();
  /**
   * 🔴 **路由登录守卫**（老板指定）：没登录直接弹去登录页，登完回到这一页。
   * ⚠️ 这一句是 hook，必须排在下面所有 early return 之前 ——
   *    放到 return 后面的话，未登录那一轮少跑一个 hook、登录后又多出来，
   *    React 直接抛 "Rendered more hooks than during the previous render"。
   */
  useRequireLogin();
  const qc = useQueryClient();

  const [rejectFor, setRejectFor] = useState<number | null>(null);
  /** 审核列表里每一行填的平台价（元，字符串）。⚠️ 只对单规格商品有意义。 */
  const [priceDraft, setPriceDraft] = useState<Record<number, string>>({});
  /** 🔴 批准退款是真的把钱退出去 —— 存整条单是为了在确认弹窗里显示金额。 */
  const [askRefund, setAskRefund] = useState<AdminRefund | null>(null);
  /** 🔴 审核通过要连平台价一起定，所以存的是整条商品不是一个 id。 */
  const [approveFor, setApproveFor] = useState<PendingProduct | null>(null);
  /** 正在编辑哪一组配置。⚠️ 一次只让改一组 —— 同时开几张表单容易存错。 */
  const [editingGroup, setEditingGroup] = useState<string | null>(null);
  const [reason, setReason] = useState("");

  /**
   * 🔴 **危险操作的理由不能用 `window.prompt` 收。**
   *    本项目明确要求驳回 / 停业 / 转移 / 没收都必须写理由（纠纷时那是唯一依据），
   *    而浏览器原生弹窗没有样式、不能校验、更要命的是
   *    **它不告诉人这一下会造成什么后果**。统一换成对话框，
   *    把后果写在标题下面那一行。
   */
  const [ask, setAsk] = useState<{
    title: string;
    description?: string;
    confirmLabel?: string;
    danger?: boolean;
    onConfirm: (reason: string) => void;
  } | null>(null);


  /** 需要「先选一个东西，再写理由」的两处：调级、转移客户。 */
  const [pick, setPick] = useState<{
    title: string;
    description?: string;
    label: string;
    options: { value: string; label: string }[];
    confirmLabel?: string;
    onConfirm: (value: string, reason: string) => void;
  } | null>(null);

  /**
   * ⚠️ **这段必须排在所有查询前面。** 下面几个查询要按权限决定发不发 ——
   *    客服没有 `finance.read`，照发的话每次进后台都会拿回一个 403。
   */
  const adminMe = useQuery({
    queryKey: ["admin", "me"],
    queryFn: () =>
      api.get<{ name: string; role: string; roleName: string; permissions: string[] }>(
        "/admin/me",
      ),
    enabled: Boolean(me),
  });
  const perms = adminMe.data?.permissions ?? [];
  /** ⚠️ `*` 是超管的通配。 */
  const can = (k: string) => perms.includes("*") || perms.includes(k);

  /**
   * 🔴 **三个审核台的数据常驻，不是点进去才取。**
   *    左栏要显示"现在有几件事等着我"—— 不摆出来的话运营只能挨个点进去看，
   *    漏审一笔提现就是有人拿不到钱。
   * ⚠️ 按权限决定发不发：客服没有 finance.read，发了只会拿回 403。
   */
  const review = useQuery({
    queryKey: ["admin", "review"],
    queryFn: () => api.get<{ items: PendingProduct[] }>("/admin/review/products?limit=50"),
    enabled: Boolean(me) && can("product.read"),
    retry: false,
  });
  const orgs = useQuery({
    queryKey: ["admin", "orgs"],
    queryFn: () => api.get<OrgRow[]>("/admin/orgs"),
    /**
     * ⚠️ **哪几页要用它，一个都不能漏。**
     *    周期费要拿节点列表选人；「客户归属」的**转移**下拉也是从它来的 ——
     *    漏了这一页的后果是：转移弹窗打开后下拉框里**一个商家都没有**，
     *    整个转移功能等于不存在，而界面上没有任何东西说明为什么。
     */
    enabled: Boolean(me) && (tab === "orgs" || tab === "fees" || tab === "bindings"),
    retry: false,
  });
  const withdrawals = useQuery({
    queryKey: ["admin", "withdrawals"],
    queryFn: () => api.get<WithdrawalRow[]>("/admin/withdrawals/pending"),
    enabled: Boolean(me) && can("finance.read"),
    retry: false,
  });
  const settings = useQuery({
    queryKey: ["admin", "settings"],
    queryFn: () => api.get<Record<string, SettingRecord>>("/admin/settings"),
    enabled: Boolean(me) && tab === "settings",
    retry: false,
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      api.post(`/admin/review/products/${id}/reject`, { reason }),
    onSuccess: () => {
      setRejectFor(null);
      setReason("");
      qc.invalidateQueries({ queryKey: ["admin"] });
    },
  });
  /**
   * 单规格商品：直接用列表那一行里填的平台价通过，不用开弹窗。
   * ⚠️ 多规格的走 `ApproveWithPriceDialog` —— 一个输入框设不了几个规格的价。
   */
  const approveOne = useMutation({
    mutationFn: ({ id, skuId, cents }: { id: number; skuId: number; cents: number }) =>
      api.post(`/admin/review/products/${id}/approve`, { platformPrices: { [skuId]: cents } }),
    onSuccess: (_d, v) => {
      /** ⚠️ 通过之后把这一行的草稿清掉，免得下次同一个 id 复用到旧值。 */
      setPriceDraft((d) => {
        const { [v.id]: _drop, ...rest } = d;
        return rest;
      });
      qc.invalidateQueries({ queryKey: ["admin"] });
      /** ⚠️ 前台那份也要作废 —— 刚上架的商品要立刻能在商城里看到。 */
      qc.invalidateQueries({ queryKey: ["products"] });
    },
  });

  const payWithdrawal = useMutation({
    mutationFn: (id: number) => api.post(`/admin/withdrawals/${id}/pay`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin"] }),
  });
  /**
   * 🔴 节点状态直接决定这个商家**能不能经营**（改价、发券、发货、拉下级）。
   *    以前这一页只有「调级」和「对账」，没有审批/停业/恢复 ——
   *    待审的商家永远开不了张，违规的商家也停不掉。
   */
  /**
   * 🔴 **拿我的权限来决定显示哪些菜单。**
   *    不过滤的话，客服会看到「提现审核」「配置中心」，点进去必然 403 ——
   *    那比看不到更糟：他会以为系统坏了，然后来问运营。
   */
  /**
   * ⚠️ 后台账号与角色的查询/变更**全部搬到 `admin-rbac.tsx`** 了 ——
   *    那两页各自取各自的数，别再在这个大组件里挂一份，
   *    否则改一处忘一处，页面上会出现两套不同步的数据。
   */

  const orgStatus = useMutation({
    mutationFn: (v: { id: number; action: "approve" | "suspend" | "resume"; reason?: string }) =>
      api.post(`/admin/orgs/${v.id}/${v.action === "resume" ? "approve" : v.action}`, {
        reason: v.reason ?? "",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "orgs"] }),
  });

  const approveWithdrawal = useMutation({
    mutationFn: (id: number) => api.post(`/admin/withdrawals/${id}/approve`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin"] }),
  });
  const runSettlement = useMutation({
    mutationFn: () => api.post<{ total: number; settled: number }>("/admin/settlement/run", {}),
  });
  const releaseDue = useMutation({
    mutationFn: () => api.post<{ released: number }>("/admin/settlement/release", {}),
  });

  const cases = useQuery({
    queryKey: ["admin", "cases"],
    queryFn: () => api.get<{ items: AdminCase[] }>("/admin/cases"),
    enabled: Boolean(me) && tab === "cases",
  });

  /** 发布/下线 —— 列表里的高频动作。新增/编辑走 `CaseEditor` 那张表单。 */
  const toggleCase = useMutation({
    mutationFn: (c: AdminCase) =>
      api.patch(`/admin/cases/${c.id}`, { published: !c.published }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "cases"] });
      /** 🔴 前台那份也要作废 —— 下线的案例要立刻从商城里消失。 */
      qc.invalidateQueries({ queryKey: ["cases"] });
    },
  });

  /**
   * 案例编辑器开着没有 / 编辑的是哪一条。
   * ⚠️ `undefined` = 关着；`null` = 新增；有值 = 编辑那一条。
   *    三态分得开才不会出现"点新增却带着上一条的内容"。
   */
  const [caseEditing, setCaseEditing] = useState<AdminCase | null | undefined>(undefined);
  const [caseToDelete, setCaseToDelete] = useState<AdminCase | null>(null);

  const delCase = useMutation({
    mutationFn: (id: number) => api.del(`/admin/cases/${id}`),
    onSuccess: () => {
      setCaseToDelete(null);
      qc.invalidateQueries({ queryKey: ["admin", "cases"] });
      qc.invalidateQueries({ queryKey: ["cases"] });
    },
  });

  // ── 等级与抽成 ──
  const levels = useQuery({
    queryKey: ["admin", "levels"],
    queryFn: () => api.get<AdminLevel[]>("/admin/levels"),
    enabled: Boolean(me) && tab === "rules",
  });
  const policies = useQuery({
    queryKey: ["admin", "fee-policies"],
    queryFn: () => api.get<AdminFeePolicy[]>("/admin/fee-policies"),
    enabled: Boolean(me) && tab === "rules",
  });
  const togglePolicy = useMutation({
    mutationFn: (p: AdminFeePolicy) =>
      api.patch(`/admin/fee-policies/${p.id}`, { enabled: !p.enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "fee-policies"] }),
  });
  /** 批量重新评级。⚠️ 默认只升不降 —— 降级会让人手里的进货价突然变贵。 */
  const evaluate = useMutation({
    /** ⚠️ changed 是**明细数组**不是数字，直接渲染会变成 [object Object]。 */
    mutationFn: () =>
      api.post<{ scanned: number; changed: { orgId: number; name: string }[] }>(
        "/admin/levels/evaluate",
        {},
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "levels"] }),
  });

  // ── 订单通知 ──
  const [notifyStatus, setNotifyStatus] = useState<string>("");
  const notify = useQuery({
    queryKey: ["admin", "notifications", notifyStatus],
    queryFn: () =>
      api.get<{ counts: Record<string, number>; configured: boolean; items: NotifyRow[] }>(
        "/admin/notifications?limit=50" + (notifyStatus ? "&status=" + notifyStatus : ""),
      ),
    enabled: Boolean(me) && tab === "notify",
  });
  const retryNotify = useMutation({
    mutationFn: (ids: number[]) =>
      api.post<{ requeued: number }>("/admin/notifications/retry", { ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "notifications"] }),
  });

  // ── 定时任务 ──
  const jobs = useQuery({
    queryKey: ["admin", "jobs"],
    queryFn: () => api.get<{ items: JobHealth[] }>("/admin/jobs"),
    enabled: Boolean(me) && tab === "jobs",
    /** ⚠️ 这一页是盯健康的，开着不刷新就失去意义了。 */
    refetchInterval: tab === "jobs" ? 15_000 : false,
  });
  const runs = useQuery({
    queryKey: ["admin", "job-runs"],
    queryFn: () => api.get<{ items: JobRunRow[] }>("/admin/jobs/runs?limit=30"),
    enabled: Boolean(me) && tab === "jobs",
  });
  const runJob = useMutation({
    mutationFn: (name: string) =>
      api.post<{
        name: string;
        label: string;
        status: string;
        result?: Record<string, unknown>;
        error?: string;
      }>(`/admin/jobs/${name}/run`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "jobs"] });
      qc.invalidateQueries({ queryKey: ["admin", "job-runs"] });
    },
  });

  // ── 周期费与保证金 ──
  const [feeOrgId, setFeeOrgId] = useState<number | null>(null);

  /** ⚠️ 出参已经按身份过滤过：分销商这里天然是空的。 */
  const feeOptions = useQuery({
    queryKey: ["admin", "fee-options", feeOrgId],
    queryFn: () => api.get<{ items: FeeOption[] }>(`/admin/orgs/${feeOrgId}/fee-options`),
    enabled: Boolean(feeOrgId),
  });
  const feeAccount = useQuery({
    queryKey: ["admin", "fee-account", feeOrgId],
    queryFn: () =>
      api.get<{ subscriptions: SubscriptionRow[]; deposits: DepositRow[]; held: number }>(
        `/admin/orgs/${feeOrgId}/fee-account`,
      ),
    enabled: Boolean(feeOrgId),
  });
  const refreshFee = () => {
    qc.invalidateQueries({ queryKey: ["admin", "fee-account", feeOrgId] });
    qc.invalidateQueries({ queryKey: ["admin", "orgs"] });
  };
  const chargeFee = useMutation({
    mutationFn: (v: { kind: string; policyId: number }) =>
      api.post(`/admin/orgs/${feeOrgId}/${v.kind === "periodic" ? "subscribe" : "deposit"}`, {
        policyId: v.policyId,
      }),
    onSuccess: refreshFee,
  });
  /** ⚠️ 现在靠人点。上线后要挂定时任务 —— 跟超时订单清理是同一类。 */
  const runBilling = useMutation({
    mutationFn: () =>
      api.post<{ charged: number; expired: number; stopped: number; amount: number }>(
        "/admin/fee-billing/run",
        {},
      ),
    onSuccess: refreshFee,
  });
  const refundDeposit = useMutation({
    mutationFn: (id: number) => api.post(`/admin/deposits/${id}/refund`, {}),
    onSuccess: refreshFee,
  });
  /** 🔴 没收必须写理由 —— 这笔钱本来是别人的，纠纷时那是唯一依据。 */
  const forfeitDeposit = useMutation({
    mutationFn: (v: { id: number; reason: string }) =>
      api.post(`/admin/deposits/${v.id}/forfeit`, { reason: v.reason }),
    onSuccess: refreshFee,
  });

  // ── 客户归属（纠纷处理） ──
  const [userKeyword, setUserKeyword] = useState("");
  const [historyFor, setHistoryFor] = useState<number | null>(null);

  const users = useQuery({
    queryKey: ["admin", "users", userKeyword],
    queryFn: () => api.get<AdminUser[]>(`/admin/users?keyword=${encodeURIComponent(userKeyword)}`),
    enabled: Boolean(me) && tab === "bindings" && userKeyword.trim().length > 0,
  });

  const history = useQuery({
    queryKey: ["admin", "binding-history", historyFor],
    queryFn: () => api.get<BindingLog[]>(`/admin/bindings/${historyFor}/history`),
    enabled: Boolean(historyFor),
  });

  const transfer = useMutation({
    mutationFn: (v: { userId: number; toOrgId: number; reason: string }) =>
      api.post("/admin/bindings/transfer", v),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "users"] });
      void qc.invalidateQueries({ queryKey: ["admin", "binding-history"] });
    },
  });

  /** 手工调级 —— 无视门槛，但必须写理由（给大客户先给待遇正是它的用途）。 */
  const setLevel = useMutation({
    mutationFn: (v: { orgId: number; levelKey: string; reason: string }) =>
      api.post(`/admin/orgs/${v.orgId}/level`, { levelKey: v.levelKey, reason: v.reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "orgs"] }),
  });

  /** 对账：流水合计是不是等于余额。 */
  const [auditFor, setAuditFor] = useState<number | null>(null);
  const audit = useQuery({
    queryKey: ["admin", "audit", auditFor],
    queryFn: () => api.get<AuditResult>(`/admin/audit/account/${auditFor}`),
    enabled: Boolean(auditFor),
  });

  const [orderKeyword, setOrderKeyword] = useState("");
  const [orderFilter, setOrderFilter] = useState("all");
  const orders = useQuery({
    queryKey: ["admin", "orders", orderKeyword, orderFilter],
    queryFn: () => {
      const qs = new URLSearchParams({ limit: "30" });
      if (orderKeyword) qs.set("keyword", orderKeyword);
      /**
       * 🔴 **付款和履约是两个正交的状态，筛选也得按这两维拼。**
       *    「待发货」= 钱收到了（payment=paid）+ 还没发（fulfillment=pending）——
       *    只按 fulfillment=pending 筛的话，待付款的单也会混进来，
       *    客服照着这张表去催发货，催的是一批还没给钱的单。
       */
      const f = ORDER_FILTERS.find((x) => x.key === orderFilter);
      if (f?.payment) qs.set("payment", f.payment);
      if (f?.fulfillment) qs.set("fulfillment", f.fulfillment);
      return api.get<{ items: AdminOrder[] }>(`/admin/orders?${qs}`);
    },
    enabled: Boolean(me) && tab === "orders",
  });

  const refunds = useQuery({
    queryKey: ["admin", "refunds"],
    queryFn: () => api.get<AdminRefund[]>("/admin/refunds"),
    enabled: Boolean(me) && can("refund.read"),
  });

  const approveRefund = useMutation({
    mutationFn: (id: number) => api.post(`/admin/refunds/${id}/approve`, {}),
    onSuccess: () => {
      setAskRefund(null);
      void qc.invalidateQueries({ queryKey: ["admin", "refunds"] });
    },
  });

  const rejectRefund = useMutation({
    mutationFn: (v: { id: number; reason: string }) =>
      api.post(`/admin/refunds/${v.id}/reject`, { reason: v.reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "refunds"] }),
  });

  const expireOverdue = useMutation({
    mutationFn: () => api.post<{ expired: number }>("/admin/orders/expire-overdue", {}),
  });

  const taxSummary = useQuery({
    queryKey: ["admin", "tax", "summary"],
    queryFn: () => api.get<TaxSummary[]>("/admin/tax/summary"),
    enabled: Boolean(me) && tab === "tax",
  });

  const taxLedger = useQuery({
    queryKey: ["admin", "tax", "ledger"],
    queryFn: () => api.get<TaxLedgerRow[]>("/admin/tax/ledger"),
    enabled: Boolean(me) && tab === "tax",
  });

  const fileTax = useMutation({
    mutationFn: (period: string) => api.post<{ filed: number }>(`/admin/tax/file/${period}`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "tax"] });
    },
  });

  /**
   * 🔴 **所有 hook 必须排在 early return 之前。**
   *    放在下面那两个 return 后面的话，未登录时这一轮少跑两个 hook，
   *    登录后又多出来 —— React 直接抛 "Rendered more hooks than during
   *    the previous render"，整页白屏。生产构建里那个报错还是压缩过的编号。
   */
  if (mePending) return <Spinner />;
  /** ⚠️ 守卫已经在跳去登录页了，这里只是跳走前那一帧 —— 别再画一个空后台壳。 */
  if (!me) return <Spinner />;

  /**
   * 🔴 **登录了、但不是后台账号 —— 这种情况绝不能再弹去登录页。**
   *    他的登录状态是好的，弹过去只会来回打转（登录成功 → 回 /admin →
   *    又被弹走）。所以单独给一屏说清楚"你不是后台的人"，并给两条出路：
   *    回商城，或者换一个有后台权限的账号登。
   *
   * ⚠️ 要等 `/admin/me` 查完再判（`isPending` 时先转圈）——
   *    不等的话，超管每次进后台都会先闪一下"没有权限"。
   * ⚠️ 判的是**查询失败**（服务端 403/404），不是"权限数组是空的"：
   *    后台账号确实可能被配成一个权限都没有，那是另一回事，
   *    该看到的是空菜单而不是"你不是后台账号"。
   */
  if (adminMe.isPending) return <Spinner />;
  if (adminMe.error) {
    return (
      <Desk title="平台后台" nav={[]}>
        <Empty
          title="这个账号没有后台权限"
          hint={`当前登录的是「${me.nickname || me.phone}」。后台账号由平台管理员在「后台账号」里开通。`}
          action={{ label: "回商城", to: "/" }}
        />
      </Desk>
    );
  }

  /**
   * 🔴 **菜单按权限过滤。**
   *    不过滤的话客服会看到「提现审核」「配置中心」，点进去必然 403 ——
   *    比看不到更糟：他会以为系统坏了，然后来问运营。
   *
   * ⚠️ 这只是**体验**上的过滤，不是安全边界 —— 真正的闸门在服务端的
   *    `requireAdmin(权限键)` 上。前端藏起来的东西，接口照样能被直接调。
   */
  /**
   * 🔴 **按"做这件事的场景"分组，不是一长条平级链接。**
   *    早先是十八条同样大小的灰色小字从头排到尾，运营要找一件事得从头读一遍，
   *    也看不出哪些是每天要盯的、哪些是配一次就不管的。
   *
   * 🔴 **三个审核台带待办数。** 后台最要紧的信息是"现在有几件事等着我"——
   *    不摆出来的话，运营只能挨个点进去看，漏审一笔提现就是有人拿不到钱。
   */
  /**
   * 节点 id → 名字。归属历史、转移下拉都用它。
   * ⚠️ 认不出来（节点已被清退删除）时退回编号，别显示成空白 ——
   *    历史记录里出现一个空格，读的人不知道是"没有"还是"坏了"。
   */
  const orgName = (id: number) =>
    (orgs.data ?? []).find((o) => o.id === id)?.name ?? `#${id}`;

  const reviewCount = review.data?.items.length ?? 0;
  const withdrawalCount = withdrawals.data?.length ?? 0;
  /** ⚠️ `/admin/refunds` 出的就是待审的那些，不用再过滤一遍。 */
  const refundCount = refunds.data?.length ?? 0;

  const nav = [
    {
      to: "/admin",
      label: "概览",
      search: { tab: "overview" },
      need: "order.read",
      icon: LayoutDashboard,
    },
    {
      to: "/admin",
      label: "商品审核",
      search: { tab: "review" },
      need: "product.read",
      icon: ClipboardCheck,
      group: "审核台",
      count: reviewCount,
    },
    {
      to: "/admin",
      label: "提现审核",
      search: { tab: "withdrawals" },
      need: "finance.read",
      icon: Banknote,
      group: "审核台",
      count: withdrawalCount,
    },
    {
      to: "/admin",
      label: "退款审核",
      search: { tab: "refunds" },
      need: "refund.read",
      icon: RotateCcw,
      group: "审核台",
      count: refundCount,
    },

    /**
     * 🔴 **前台天天在用、后台以前一个入口都没有的三样。**
     *    品类是整个前台浏览的骨架，品牌挂在每件商品上，运费模板决定每单收多少运费 ——
     *    以前运营想开一个新品类只能连数据库改。
     */
    {
      to: "/admin",
      label: "全站商品",
      search: { tab: "all-products" },
      need: "product.read",
      icon: Package,
      group: "商品基础",
    },
    {
      to: "/admin",
      label: "品类",
      search: { tab: "categories" },
      need: "product.read",
      icon: LayoutGrid,
      group: "商品基础",
    },
    {
      to: "/admin",
      label: "品牌",
      search: { tab: "brands" },
      need: "product.read",
      icon: Bookmark,
      group: "商品基础",
    },
    {
      to: "/admin",
      label: "运费模板",
      search: { tab: "freight" },
      need: "product.read",
      icon: Truck,
      group: "商品基础",
    },

    {
      to: "/admin",
      label: "订单查询",
      search: { tab: "orders" },
      need: "order.read",
      icon: ClipboardList,
      group: "交易与资金",
    },
    {
      to: "/admin",
      label: "结算",
      search: { tab: "settlement" },
      need: "finance.read",
      icon: Wallet,
      group: "交易与资金",
    },
    {
      to: "/admin",
      label: "个税台账",
      search: { tab: "tax" },
      need: "tax.read",
      icon: Receipt,
      group: "交易与资金",
    },

    {
      to: "/admin",
      label: "组织树",
      search: { tab: "orgs" },
      need: "org.read",
      icon: Network,
      group: "组织与客户",
    },
    {
      to: "/admin",
      label: "用户",
      search: { tab: "users" },
      need: "binding.read",
      icon: Users,
      group: "组织与客户",
    },
    {
      to: "/admin",
      label: "客户归属",
      search: { tab: "bindings" },
      need: "binding.read",
      icon: Link2,
      group: "组织与客户",
    },

    {
      to: "/admin",
      label: "装修案例",
      search: { tab: "cases" },
      need: "case.read",
      icon: Images,
      group: "运营",
    },
    {
      to: "/admin",
      label: "价格规则",
      search: { tab: "price-rules" },
      need: "policy.read",
      icon: Scale,
      group: "运营",
    },
    {
      to: "/admin",
      label: "等级与抽成",
      search: { tab: "rules" },
      need: "policy.read",
      icon: BadgePercent,
      group: "运营",
    },
    {
      to: "/admin",
      label: "周期费与保证金",
      search: { tab: "fees" },
      need: "policy.read",
      icon: Coins,
      group: "运营",
    },

    /*
      🔴 **「进货」「定价」「优惠券」不在这儿。**
         平台老板确实要做这三件事（他向供应商按供货价拿货、给下级定价、发券），
         但那是他的**商家身份**，走的是前台的商家端页面 ——
         塞进后台导航的结果是：在后台点一下优惠券，整个界面换成了商城的账户中心，
         `/restock` 还会顺手把身份切成"进货"然后跳到商品列表。
         现在收到侧栏底部的「离开后台」区，点之前就知道会跳走。
    */

    {
      to: "/admin",
      label: "定时任务",
      search: { tab: "jobs" },
      need: "system.read",
      icon: Timer,
      group: "系统",
    },
    {
      to: "/admin",
      label: "订单通知",
      search: { tab: "notify" },
      need: "system.read",
      icon: Mail,
      group: "系统",
    },
    {
      to: "/admin",
      label: "平台公告",
      search: { tab: "announce" },
      need: "message.broadcast",
      icon: Megaphone,
      group: "系统",
    },
    {
      to: "/admin",
      label: "系统健康",
      search: { tab: "health" },
      need: "system.read",
      icon: Activity,
      group: "系统",
    },
    {
      /**
       * 🔴 **登录页那三样以前写死在组件里**，改一个字要发一次版 ——
       *    而它是整个站第一眼看到的地方，运营改文案的频率远高于发版。
       * ⚠️ 单开一个「页面管理」分组：以后还会有别的页要配（首页横幅、
       *    关于我们…），塞进「系统」的话那一组会越堆越杂。
       */
      to: "/admin",
      label: "登录页管理",
      search: { tab: "login-page" },
      need: "system.read",
      icon: LayoutTemplate,
      group: "页面管理",
    },
    {
      /**
       * 🔴 **首页那一排「按空间挑」以前是写死的五个 emoji。**
       *    emoji 的字形由系统字体决定 —— 同一个 🛋️ 在 iOS、Windows、
       *    部分安卓上画出来是三个画风，而它们本该是一组；颜色也不受控，
       *    压在主色底板上怎么摆都是脏的，换配色时还不跟着走。
       *    现在换成矢量图标，并且**图标、名字、顺序都能在这里改**。
       */
      to: "/admin",
      label: "首页空间",
      search: { tab: "spaces" },
      need: "system.read",
      icon: Sofa,
      group: "页面管理",
    },
    {
      /**
       * 🔴 **配色要看着挑，所以单独一页而不是配置中心里的一个下拉框。**
       *    老板：「给网站设置几种常见配色，可以切换…这个是平台管理后台来切换」。
       */
      to: "/admin",
      label: "外观",
      search: { tab: "appearance" },
      need: "system.read",
      icon: Palette,
      group: "系统",
    },
    {
      to: "/admin",
      label: "配置中心",
      search: { tab: "settings" },
      need: "system.read",
      icon: Settings2,
      group: "系统",
    },
    {
      to: "/admin",
      label: "后台账号",
      search: { tab: "admins" },
      need: "admin.manage",
      icon: KeyRound,
      group: "系统",
    },
    {
      /**
       * 🔴 **账号和角色分成两页。** 老板：「一个账号管理一个角色管理」。
       *    挤在一页里的时候，角色只是下面四张读不动的卡片 ——
       *    而"这个角色能干什么"恰恰是这套权限里最需要调的东西。
       */
      to: "/admin",
      label: "角色权限",
      search: { tab: "roles" },
      need: "admin.manage",
      icon: ShieldCheck,
      group: "系统",
    },
  ]
    .filter((n) => can(n.need))
    .map(({ need: _need, ...rest }) => rest);

  /**
   * 🔴 **只显示当前这一页自己的错。**
   *    早先是把五个查询的 error 一股脑 `??` 在一起 —— 于是在「订单查询」页上
   *    会看到一条红色的「服务器开小差了」，而那其实是**别的页**某个查询失败留下的
   *    （`retry: false`，错误对象一直挂着）。页面自己好好的、上面顶着一条报错，
   *    运营只会以为整个后台坏了。
   */
  const tabError =
    tab === "review"
      ? review.error
      : tab === "orgs" || tab === "fees"
        ? orgs.error
        : tab === "withdrawals"
          ? withdrawals.error
          : tab === "settings"
            ? settings.error
            : tab === "cases"
              ? cases.error
              : tab === "refunds"
                ? refunds.error
                : null;

  return (
    <Desk
      title="平台后台"
      subtitle={
        adminMe.data ? `${adminMe.data.name} · ${adminMe.data.roleName}` : undefined
      }
      nav={nav}
      defaultTab="overview"
      /*
        ⚠️ 平台自己的经营（进货 / 定价 / 发券）是**商家身份**的事，
           在商城的「我的」里切到「装修建材商城」这个身份做。
           这里只给一条明确标着"另一套界面"的出口。
      */
      exits={[{ to: "/me", label: "平台自营", hint: "进货 / 定价 / 发券，在商家端做" }]}
    >
      {/* ⚠️ 非平台成员访问会 403，直接把后端那句话显示出来，别自己编 */}
      {tabError && <ErrorBlock error={tabError} />}

      {tab === "overview" && <OverviewTab />}
      {tab === "all-products" && <AllProductsTab />}
      {tab === "price-rules" && <PriceRulesTab />}
      {tab === "users" && <UsersTab />}
      {tab === "announce" && <AnnouncementTab />}
      {tab === "health" && <HealthTab />}
      {tab === "categories" && <CategoriesTab />}
      {tab === "brands" && <BrandsTab />}
      {tab === "freight" && <FreightTab />}

      {tab === "review" && !review.error && (
        /*
          🔴 **占满这一屏，别让下面空一大片。**（老板圈过商品审核这一页）
             待审的商品常常只有零星几件甚至一件都没有，表格就那么高，
             底下小半屏是白的 —— 看着像页面没加载完。

          ⚠️ **高度按视口算，不要用 `flex-1`。** 后台内容区跟**左侧导航**在
             同一个 flex 行里，而那个导航有二十来项、一千多像素高 ——
             `flex-1` 会把这一块拉到 1100px 直接溢出屏幕，比空着还糟。
             （这个坑在空态卡片上已经真踩过一次。）
             减掉的 8.5rem ≈ 顶部抬头 64px + 内容区上下内边距 48px。
        */
        <div className="flex min-h-[calc(100dvh-8.5rem)] flex-col gap-4">
          {review.isPending ? (
            <Spinner compact />
          ) : review.data.items.length === 0 ? (
            <Empty title="没有待审核的商品" hint="供应商提交后会出现在这里" compact fill />
          ) : (
            <Table
              fill
              head={["商品", "提交人", "最低供货价", "平台价", "提交时间", "操作"]}
            >
              {review.data.items.map((p) => (
                <tr key={p.id} className="hover:bg-neutral-50">
                  <Td>
                    {/* ⚠️ 带上图 —— 审核第一件事就是看这货长什么样，只有名字判断不了。 */}
                    <div className="flex items-center gap-3">
                      <Img
                        src={p.imageUrls?.[0]}
                        alt={p.title}
                        className="size-11 shrink-0 rounded-lg border border-border object-cover"
                      />
                      <span className="min-w-0">{p.title}</span>
                    </div>
                  </Td>
                  <Td>{p.supplierName}</Td>
                  <Td className="tabular-nums"><Price cents={p.minSupplyPrice} /></Td>
                  {/*
                    🔴 **平台价在这一行直接填。** 老板：「平台价格是平台设置的，
                       需要平台管理员设置平台价格，另外不需要供应商建议售价」。

                       所以这里是个**空的输入框**，不预填供应商写过的数 ——
                       预填出来的话审核就变成了点一下「通过」的橡皮图章，
                       而这个数是**全站的价格天花板**（游客付它、所有人的划线价
                       是它），必须是平台自己的决定。

                    ⚠️ **多规格填不进一个框里。** 一件商品可能有好几个规格，
                       它们的平台价本来就不一样 —— 那种情况给一颗「逐规格定价」
                       按钮开弹窗，别拿一个数糊到所有规格上。
                  */}
                  <Td>
                    {p.onlySkuId ? (
                      /*
                        ⚠️ **货币符号做成输入框里的前缀，不在后面挂一个"元/件"。**
                           挂在后面的那种写法有两个毛病，老板都点出来了：
                           ① 输入框里的 placeholder 跟右对齐的数字打架，看着歪；
                           ② "元"是写死的 —— 这个站的货币是**后台「外观」里配的**
                              （`CNY` → ¥ / `TWD` → NT$），写死"元"的话，
                              切到台币之后整站都换了符号，只有这一处还写着"元"。
                        ⚠️ 数字**右对齐 + `tabular-nums`**：几行价格要能竖着对齐比大小。
                           placeholder 去掉了 —— 前缀符号已经说清楚这一格填什么。
                      */
                      <div className="flex h-9 w-[132px] items-center rounded-lg border border-input bg-background pr-2 pl-2.5 focus-within:border-brand-500">
                        <span className="shrink-0 text-[13px] text-muted-foreground">
                          {currencySymbol()}
                        </span>
                        <input
                          inputMode="decimal"
                          className="min-w-0 flex-1 border-0 bg-transparent text-right text-[14px] tabular-nums outline-none"
                          value={priceDraft[p.id] ?? ""}
                          onChange={(e) =>
                            setPriceDraft((d) => ({ ...d, [p.id]: e.target.value }))
                          }
                        />
                      </div>
                    ) : (
                      <button
                        className="text-[13px] text-brand-600 hover:underline"
                        onClick={() => setApproveFor(p)}
                      >
                        逐规格定价（{p.skuCount} 个）
                      </button>
                    )}
                  </Td>
                  <Td className="text-xs text-ink-500">
                    {new Date(p.createdAt).toLocaleString("zh-CN")}
                  </Td>
                  <Td>
                    {rejectFor === p.id ? (
                      <div className="flex gap-1">
                        <input
                          autoFocus
                          className="h-9 w-48 rounded-md border border-neutral-300 px-2 text-xs"
                          placeholder="驳回理由（会原样给供应商看）"
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                        />
                        <Button
                          size="sm"
                          variant="danger"
                          disabled={!reason.trim()}
                          onClick={() => reject.mutate({ id: p.id, reason })}
                        >
                          确认驳回
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setRejectFor(null)}>
                          取消
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-1">
                        {/*
                          🔴 **通过 = 顺手把平台价定下来。**
                             老板：「审核时就会有平台价，审核通过才会上架平台」。
                             单规格的直接用这一行里填的那个数；多规格的开弹窗逐个填。
                          ⚠️ **没填价就不许点** —— 服务端也会拒（缺平台价的规格
                             `approveProduct` 直接抛），前端先禁用是为了不让他
                             白点一次再看一句红字。
                        */}
                        <Button
                          size="sm"
                          loading={approveOne.isPending && approveOne.variables?.id === p.id}
                          disabled={Boolean(p.onlySkuId) && !toCents(priceDraft[p.id] ?? "")}
                          onClick={() =>
                            p.onlySkuId
                              ? approveOne.mutate({
                                  id: p.id,
                                  skuId: p.onlySkuId,
                                  cents: toCents(priceDraft[p.id] ?? "")!,
                                })
                              : setApproveFor(p)
                          }
                        >
                          通过
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setRejectFor(p.id)}>
                          驳回
                        </Button>
                      </div>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          )}
          {reject.error && <ErrorBlock error={reject.error} />}

          <ApproveWithPriceDialog
            product={approveFor}
            onClose={() => setApproveFor(null)}
            onDone={() => {
              void qc.invalidateQueries({ queryKey: ["admin"] });
              /** ⚠️ 前台那份也要作废 —— 刚上架的商品要立刻能在商城里看到。 */
              void qc.invalidateQueries({ queryKey: ["products"] });
            }}
          />
        </div>
      )}

      {tab === "orgs" && !orgs.error && (
        <>
          {orgs.isPending ? (
            <Spinner compact />
          ) : (
            <Table head={["层级", "名称", "类型", "状态", "等级", "邀请码", ""]}>
              {orgs.data.map((o) => (
                <tr key={o.id} className="hover:bg-neutral-50">
                  <Td className="text-xs text-ink-500">L{o.depth}</Td>
                  <Td>
                    {/* 用缩进表现层级 —— 树在表格里最直观的画法 */}
                    <span style={{ paddingLeft: `${o.depth * 16}px` }}>
                      {o.depth > 0 && <span className="text-ink-500">└ </span>}
                      {o.name}
                    </span>
                  </Td>
                  <Td>
                    <Badge tone={o.type === "platform" ? "ok" : "mute"}>
                      {TYPE_LABEL[o.type] ?? o.type}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge
                      tone={
                        o.status === "active" ? "ok" : o.status === "pending" ? "warn" : "mute"
                      }
                    >
                      {o.status === "active"
                        ? "正常"
                        : o.status === "pending"
                          ? "待审核"
                          : o.status === "suspended"
                            ? "已停用"
                            : "已清退"}
                    </Badge>
                  </Td>
                  <Td className="text-xs">{o.levelKey || "—"}</Td>
                  <Td className="font-mono text-xs">{o.inviteCode}</Td>
                  <Td className="whitespace-nowrap">
                    <span className="flex justify-end gap-1.5">
                    {/* 手工调级：无视门槛给大客户先给待遇，但必须写理由 */}
                    {(o.type === "dealer" || o.type === "distributor") && (
                      <button
                        className="inline-flex h-7 items-center rounded-md border px-2.5 text-[12px] whitespace-nowrap transition-colors disabled:opacity-40 border-brand-200 text-brand-700 hover:border-brand-300 hover:bg-brand-50"
                        onClick={() =>
                          setPick({
                            title: `给「${o.name}」调级`,
                            description:
                              "等级决定他能拿到的进货价和佣金比例 —— 调完下一单立刻按新等级算。理由会记进变更日志。",
                            label: "调到哪个等级",
                            options: (levels.data ?? []).map((l) => ({
                              value: l.key,
                              label: `${l.name}（${l.key}）`,
                            })),
                            confirmLabel: "确认调级",
                            onConfirm: (levelKey, reason) =>
                              setLevel.mutate({ orgId: o.id, levelKey, reason }),
                          })
                        }
                        disabled={setLevel.isPending}
                      >
                        调级
                      </button>
                    )}
                    {/*
                      🔴 待审的商家什么都干不了 —— 审批按钮必须在这里，
                         否则他永远开不了张，而运营根本不知道卡在哪。
                    */}
                    {o.type !== "platform" && o.status === "pending" && (
                      <button
                        className="inline-flex h-7 items-center rounded-md border px-2.5 text-[12px] whitespace-nowrap transition-colors disabled:opacity-40 border-emerald-200 text-emerald-700 hover:border-emerald-300 hover:bg-emerald-50"
                        onClick={() => orgStatus.mutate({ id: o.id, action: "approve" })}
                      >
                        通过审核
                      </button>
                    )}
                    {o.type !== "platform" && o.status === "active" && (
                      <button
                        className="inline-flex h-7 items-center rounded-md border px-2.5 text-[12px] whitespace-nowrap transition-colors disabled:opacity-40 border-destructive/25 text-destructive hover:border-destructive/40 hover:bg-destructive/5"
                        onClick={() =>
                          /* ⚠️ 停业是处罚，必须写理由 —— 会记进变更日志 */
                          setAsk({
                            title: `让「${o.name}」停业整顿`,
                            description:
                              "停业之后他改价、发券、上架、发货、拉下级全都做不了（已经赚的钱仍然能提现）。理由会记进变更日志。",
                            confirmLabel: "确认停业",
                            danger: true,
                            onConfirm: (reason) =>
                              orgStatus.mutate({ id: o.id, action: "suspend", reason }),
                          })
                        }
                      >
                        停业
                      </button>
                    )}
                    {o.type !== "platform" && o.status === "suspended" && (
                      <button
                        className="inline-flex h-7 items-center rounded-md border px-2.5 text-[12px] whitespace-nowrap transition-colors disabled:opacity-40 border-emerald-200 text-emerald-700 hover:border-emerald-300 hover:bg-emerald-50"
                        onClick={() => orgStatus.mutate({ id: o.id, action: "resume" })}
                      >
                        恢复经营
                      </button>
                    )}
                    {o.type !== "platform" && (
                      <button className="inline-flex h-7 items-center rounded-md border px-2.5 text-[12px] whitespace-nowrap transition-colors disabled:opacity-40 border-border text-muted-foreground hover:bg-secondary hover:text-foreground" onClick={() => setAuditFor(o.id)}>
                        对账
                      </button>
                    )}
                    </span>
                  </Td>
                </tr>
              ))}
            </Table>
          )}
          {setLevel.error && <ErrorBlock error={setLevel.error} />}

          {/*
            🔴 对账查的是"流水合计 == 余额"这条等式。不成立说明某处改余额
               没走流水 —— 那是资金问题，要立刻查。
          */}
          {auditFor && (
            <div className="mt-4 max-w-2xl rounded-lg border border-neutral-200 bg-white p-5">
              <div className="mb-2 flex items-center justify-between">
                <h2 className="text-sm font-medium">账户对账</h2>
                <button className="inline-flex h-7 items-center rounded-md border px-2.5 text-[12px] whitespace-nowrap transition-colors disabled:opacity-40 border-border text-muted-foreground hover:bg-secondary hover:text-foreground" onClick={() => setAuditFor(null)}>
                  关闭
                </button>
              </div>
              {audit.isPending ? (
                <Spinner compact />
              ) : audit.error ? (
                <ErrorBlock error={audit.error} />
              ) : (
                <dl className="space-y-1.5 text-xs">
                  <div className="flex justify-between">
                    <dt className="text-ink-500">流水笔数</dt>
                    <dd>{audit.data!.txnCount}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-ink-500">流水合计</dt>
                    <dd>
                      <Price cents={audit.data!.txnSum} />
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-ink-500">账户余额</dt>
                    <dd>
                      <Price cents={audit.data!.accountBalance} />
                    </dd>
                  </div>
                  <div className="flex justify-between border-t border-neutral-100 pt-1.5">
                    <dt className="text-ink-500">是否平账</dt>
                    <dd>
                      {audit.data!.matched ? (
                        <Badge tone="ok">平</Badge>
                      ) : (
                        <Badge tone="warn">不平，需立刻排查</Badge>
                      )}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-ink-500">流水链逐条自洽</dt>
                    <dd>{audit.data!.chain.ok ? <Badge tone="ok">是</Badge> : <Badge tone="warn">否</Badge>}</dd>
                  </div>
                </dl>
              )}
            </div>
          )}
        </>
      )}

      {tab === "withdrawals" && !withdrawals.error && (
        /*
          🔴 **撑满这一屏，别在下面留半屏白。**（老板圈的）
             审核队列常常只有零星几条甚至一条没有，表格就那么高，
             底下小半屏是白的 —— 看着像页面没加载完。

          ⚠️ **高度按视口算，不要用 `flex-1`。** 后台内容区跟左侧导航在同一个
             flex 行里，而那个导航一千多像素高 —— `flex-1` 会把这一块拉到
             1100px 直接溢出屏幕，比空着还糟（这个坑真踩过一次）。
             减掉的 8.5rem ≈ 顶部抬头 64px + 内容区上下内边距 48px。
        */
        <div className="flex min-h-[calc(100dvh-8.5rem)] flex-col gap-4">
          {withdrawals.isPending ? (
            <Spinner compact />
          ) : withdrawals.data.length === 0 ? (
            <Empty
              title="没有待审核的提现"
              hint="商家申请提现后会出现在这里。"
              compact
              fill
            />
          ) : (
            <Table fill head={["提现单号", "商家", "金额", "手续费", "实付", "收款方式", "操作"]}>
              {withdrawals.data.map((w) => (
                <tr key={w.id} className="hover:bg-neutral-50">
                  <Td className="font-mono text-xs">{w.withdrawalNo}</Td>
                  <Td className="text-xs">#{w.orgId}</Td>
                  <Td className="tabular-nums"><Price cents={w.amount} /></Td>
                  <Td className="tabular-nums text-ink-500"><Price cents={w.fee} /></Td>
                  <Td className="tabular-nums"><Price cents={w.actualAmount} /></Td>
                  <Td className="text-xs">
                    {/* ⚠️ 账号脱敏 —— 后台列表不需要看到完整卡号 */}
                    {w.payeeSnapshot.name} ·{" "}
                    {String(w.payeeSnapshot.account ?? "").replace(/^(.{3}).*(.{4})$/, "$1****$2")}
                  </Td>
                  <Td>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => approveWithdrawal.mutate(w.id)}>
                        审核通过
                      </Button>
                      <Button size="sm" onClick={() => payWithdrawal.mutate(w.id)}>
                        已打款
                      </Button>
                    </div>
                  </Td>
                </tr>
              ))}
            </Table>
          )}
          {(approveWithdrawal.error || payWithdrawal.error) && (
            <ErrorBlock error={approveWithdrawal.error ?? payWithdrawal.error} />
          )}
        </div>
      )}

      {tab === "bindings" && (
        <>
        <div className="flex min-h-[calc(100dvh-8.5rem)] flex-col">
          {/*
            🔴 归属是这个平台的核心资产之一，纠纷必然会有：
               两个分销商争一个客户、客户说自己被错误归属。
               处理流程是"搜到人 → 看他怎么绑上去的 → 决定转不转"。
          */}

          <div className="mb-4 max-w-md">
            <Input
              placeholder="按手机号或昵称搜客户"
              value={userKeyword}
              onChange={(e) => setUserKeyword(e.target.value)}
            />
          </div>

          {userKeyword.trim() === "" ? (
            <Empty
              title="按手机号或昵称搜一个客户"
              hint="这一页处理归属纠纷：看他当初是被谁拉来的、什么时候绑的，必要时转给另一家商家。"
              compact
              fill
            />
          ) : users.isPending ? (
            <Spinner compact />
          ) : (users.data?.length ?? 0) === 0 ? (
            <Empty title="没搜到这个人" hint="客服手上通常只有手机号，尾号也能搜。" compact fill />
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-ink-500">
                <tr>
                  <th className="py-2">客户</th>
                  <th>手机号</th>
                  <th>当前归属</th>
                  <th>归属时间</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {users.data!.map((u) => (
                  <tr key={u.userId} className="border-t border-neutral-100">
                    <td className="py-2.5">{u.nickname || "未设昵称"}</td>
                    <td className="font-mono text-xs">{u.phone ?? "—"}</td>
                    <td className="text-xs">
                      {u.boundOrgName ?? <span className="text-ink-500">无归属（按平台价）</span>}
                    </td>
                    <td className="text-xs text-ink-500">
                      {u.boundAt ? new Date(u.boundAt).toLocaleDateString("zh-CN") : "—"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="flex justify-end gap-1.5">
                      <button
                        className="inline-flex h-7 items-center rounded-md border px-2.5 text-[12px] whitespace-nowrap transition-colors disabled:opacity-40 border-border text-muted-foreground hover:bg-secondary hover:text-foreground"
                        onClick={() => setHistoryFor(historyFor === u.userId ? null : u.userId)}
                      >
                        {historyFor === u.userId ? "收起" : "归属历史"}
                      </button>
                      <button
                        className="inline-flex h-7 items-center rounded-md border px-2.5 text-[12px] whitespace-nowrap transition-colors disabled:opacity-40 border-brand-200 text-brand-700 hover:border-brand-300 hover:bg-brand-50"
                        onClick={() =>
                          setPick({
                            title: `把「${u.nickname || u.phone}」转给别的商家`,
                            description:
                              "🔴 转移只改以后，不动过去 —— 已经下过的单仍然按当时的链快照分账。理由会记进变更日志。",
                            label: "转给哪一家",
                            options: (orgs.data ?? [])
                              .filter((x) => x.type === "dealer" || x.type === "distributor")
                              .map((x) => ({ value: String(x.id), label: `${x.name}（#${x.id}）` })),
                            confirmLabel: "确认转移",
                            onConfirm: (toOrgId, reason) =>
                              transfer.mutate({ userId: u.userId, toOrgId: Number(toOrgId), reason }),
                          })
                        }
                        disabled={transfer.isPending}
                      >
                        转移
                      </button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {transfer.error && <ErrorBlock error={transfer.error} />}
          {transfer.isSuccess && (
            <p className="mt-2 text-xs text-emerald-700">已转移，之后的订单按新归属分账。</p>
          )}

          {/* 归属变更历史 —— 纠纷时的唯一依据 */}
          {historyFor && (
            <div className="mt-4 rounded-lg border border-neutral-200 bg-white p-5">
              <h2 className="mb-2 text-sm font-medium">归属变更历史</h2>
              {history.isPending ? (
                <Spinner compact />
              ) : (history.data?.length ?? 0) === 0 ? (
                <p className="text-xs text-ink-500">没有变更记录。</p>
              ) : (
                <ol className="space-y-2 text-xs">
                  {history.data!.map((h) => (
                    <li key={h.id} className="flex gap-3">
                      <span className="shrink-0 text-ink-500">
                        {new Date(h.createdAt).toLocaleString("zh-CN")}
                      </span>
                      {/*
                        🔴 **出名字，不出 `#15308`。** 这条历史是纠纷时的**唯一依据** ——
                           两个分销商争一个客户时，运营要照着它判"这个人当初是谁拉来的"。
                           一串编号没法判，他得另开一页去组织树里一个个对。
                        ⚠️ 认不出来（节点已被删）才退回编号，别显示成空白。
                      */}
                      <span className="flex-1">
                        {h.fromOrgId ? orgName(h.fromOrgId) : "无归属"} →{" "}
                        {h.toOrgId ? orgName(h.toOrgId) : "无归属"}
                        {h.reason && <span className="ml-2 text-ink-500">{h.reason}</span>}
                      </span>
                      <span className="shrink-0 text-ink-500">{h.operator}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          )}
        </div>
        </>
      )}

      {tab === "orders" && (
        <>

          {/*
            ⚠️ **筛选和搜索在同一行**：客服的动作是"先缩到一类，再在里面找"，
               两个控件隔开的话每次都要上下扫一遍。跟「全站商品」那一页同一套布局。
          */}
          <div className="mb-4 flex items-center gap-4">
            <div className="flex flex-wrap gap-1.5">
              {ORDER_FILTERS.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setOrderFilter(f.key)}
                  className={cn(
                    "rounded-lg px-3 py-1.5 text-[13px] transition-colors",
                    orderFilter === f.key
                      ? "bg-brand-600 text-white"
                      : "text-muted-foreground hover:bg-secondary hover:text-foreground",
                  )}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="relative ml-auto w-[280px] shrink-0">
              <Search className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="h-9 pl-10"
                placeholder="搜订单号或收货人手机号"
                value={orderKeyword}
                onChange={(e) => setOrderKeyword(e.target.value)}
              />
            </div>
          </div>

          {orders.error ? (
            <ErrorBlock error={orders.error} />
          ) : orders.isPending ? (
            <Spinner compact />
          ) : (orders.data?.items.length ?? 0) === 0 ? (
            /*
              🔴 **空态是一张满宽的卡，上下居中** —— 见 `Empty` 上的长注释。
                 原先这一页（和后台另外十几页）都是几行字浮在内容区左上角：
                 横着偏左、竖着贴着标题栏，底下一大片空白，看着像没加载完。
            */
            <Empty
              compact
              title={orderKeyword || orderFilter !== "all" ? "没有符合条件的订单" : "还没有订单"}
              hint={
                orderKeyword
                  ? "订单号和收货人手机号都支持尾号搜索，只记得后四位也能查到。"
                  : orderFilter !== "all"
                    ? "换一个状态看看，或者清空筛选看全部。"
                    : "买家下单之后，全站的订单都会出现在这里。"
              }
              /** ⚠️ 带筛选时一定要给出路，否则点了个没数据的状态只能自己想起来点回「全部」。 */
              action={
                orderKeyword || orderFilter !== "all"
                  ? {
                      label: "清空筛选",
                      onClick: () => {
                        setOrderKeyword("");
                        setOrderFilter("all");
                      },
                    }
                  : undefined
              }
            />
          ) : (
            <Table
              head={["订单号", "商品", "收货人", "实付", "已退", "状态", "下单时间"]}
            >
              {orders.data!.items.map((o) => (
                <Tr key={o.id}>
                  {/* ⚠️ 等宽字体 + 可选中：客服要把单号复制到聊天里对给买家。 */}
                  <Td className="font-mono text-[12px] whitespace-nowrap select-all">
                    {o.orderNo}
                  </Td>
                  {/*
                    ⚠️ 列表里带商品名，客服一眼能对上买家说的"我买的那个橱柜" ——
                       只有订单号的话每一单都要点进去看。
                  */}
                  <Td className="max-w-[260px]">
                    <span className="block truncate text-[13px]">
                      {o.items[0]?.titleSnapshot ?? "—"}
                    </span>
                    {o.items.length > 1 && (
                      <span className="text-[12px] text-muted-foreground">
                        等 {o.items.length} 件
                      </span>
                    )}
                  </Td>
                  <Td className="whitespace-nowrap">
                    <span className="block text-[13px]">{o.shipName}</span>
                    <span className="text-[12px] text-muted-foreground">{o.shipPhone}</span>
                  </Td>
                  <Td className="tabular-nums whitespace-nowrap">
                    <Price cents={o.payableAmount} />
                  </Td>
                  <Td className="tabular-nums whitespace-nowrap text-muted-foreground">
                    {o.refundedAmount > 0 ? <Price cents={o.refundedAmount} /> : "—"}
                  </Td>
                  <Td>
                    <OrderStatus
                      payment={o.payment as never}
                      fulfillment={o.fulfillment as never}
                    />
                  </Td>
                  <Td className="whitespace-nowrap text-[12px] text-muted-foreground">
                    {new Date(o.createdAt).toLocaleString("zh-CN")}
                  </Td>
                </Tr>
              ))}
            </Table>
          )}
        </>
      )}

      {tab === "refunds" && (
        <>
        {/*
          🔴 **撑满这一屏，别在下面留半屏白。**（老板圈的）
             审核队列常常只有零星几条甚至一条没有，表格就那么高，
             底下小半屏是白的 —— 看着像页面没加载完。

          ⚠️ **高度按视口算，不要用 `flex-1`。** 后台内容区跟左侧导航在同一个
             flex 行里，而那个导航一千多像素高 —— `flex-1` 会把这一块拉到
             1100px 直接溢出屏幕，比空着还糟（这个坑真踩过一次）。
             减掉的 8.5rem ≈ 顶部抬头 64px + 内容区上下内边距 48px。
        */}
        <div className="flex min-h-[calc(100dvh-8.5rem)] flex-col gap-4">
          {refunds.isPending ? (
            <Spinner compact />
          ) : (refunds.data?.length ?? 0) === 0 ? (
            <Empty compact fill title="没有待审核的退款" hint="买家申请退款后会出现在这里。" />
          ) : (
            /* ⚠️ 换成后台统一的 `Table` —— 原来是一张裸 `<table>`，
                  行高、表头、hover 都跟别的页对不上。 */
            <Table
              fill
              head={["退款单", "订单号", "实付", "已退", "本次退", "原因", "操作"]}
            >
                {refunds.data!.map((r) => (
                  <tr key={r.id} className="hover:bg-neutral-50">
                    <Td className="font-mono text-xs select-all">{r.refundNo}</Td>
                    <Td className="font-mono text-xs select-all">{r.orderNo}</Td>
                    <Td className="tabular-nums">
                      <Price cents={r.paidAmount} />
                    </Td>
                    <Td className="text-xs tabular-nums text-ink-500">
                      <Price cents={r.alreadyRefunded} />
                    </Td>
                    <Td className="tabular-nums text-brand-600">
                      <Price cents={r.amount} />
                    </Td>
                    <Td className="max-w-[16rem] truncate text-xs">{r.reason}</Td>
                    <Td>
                      <span className="flex justify-end gap-1.5">
                      <button
                        className="inline-flex h-7 items-center rounded-md border px-2.5 text-[12px] whitespace-nowrap transition-colors disabled:opacity-40 border-brand-200 text-brand-700 hover:border-brand-300 hover:bg-brand-50"
                        /*
                          🔴 **批准退款是真的把钱退出去，不能点一下就执行。**
                             原先没有任何确认 —— 而这一下同时做四件事：
                             落退款单、改订单付款状态、扣回各级分账、把钱退给买家。
                             那句"可能把商家余额扣成负数"的提醒原来贴在页顶当页面说明，
                             没人在按之前会去读它；挪到这里才是人真正会看到的位置。
                        */
                        onClick={() => setAskRefund(r)}
                        disabled={approveRefund.isPending}
                      >
                        批准
                      </button>
                      <button
                        className="inline-flex h-7 items-center rounded-md border px-2.5 text-[12px] whitespace-nowrap transition-colors disabled:opacity-40 border-border text-muted-foreground hover:bg-secondary hover:text-foreground"
                        onClick={() =>
                          setAsk({
                            title: "驳回这笔退款",
                            description:
                              "买家会在站内信里看到你写的理由。写不清楚他只会反复申请，客服也答不上来。",
                            confirmLabel: "确认驳回",
                            danger: true,
                            onConfirm: (reason) => rejectRefund.mutate({ id: r.id, reason }),
                          })
                        }
                        disabled={rejectRefund.isPending}
                      >
                        驳回
                      </button>
                      </span>
                    </Td>
                  </tr>
                ))}
            </Table>
          )}
          <ReasonDialog
            open={Boolean(askRefund)}
            title={`批准退款 ${askRefund ? fen(askRefund.amount) : ""}`}
            description="钱立刻退给买家，并从各级商家的分账里按比例扣回。⚠️ 如果那笔钱已经结算或被提走，对应商家的余额会变负 —— 这是正常的，表示他欠平台钱，下次有收入时自动冲抵。整单退还会把还没发的货停掉。"
            placeholder="写清楚原因，记录在退款单上"
            confirmLabel="确认退款"
            danger
            loading={approveRefund.isPending}
            onConfirm={() => askRefund && approveRefund.mutate(askRefund.id)}
            onClose={() => setAskRefund(null)}
          />

          {(approveRefund.error || rejectRefund.error) && (
            <ErrorBlock error={approveRefund.error ?? rejectRefund.error} />
          )}
        </div>
        </>
      )}

      {tab === "settlement" && (
        <>
        {/*
          ⚠️ 两张动作卡**并排**，不要竖着堆在左边那 672px 里 ——
             页面右边会空出一大片，看着像内容没加载完。
        */}
        <div className="grid items-start gap-4 lg:grid-cols-2 xl:grid-cols-3">
          <section className="rounded-lg border border-neutral-200 bg-white p-5">
            <h2 className="text-sm font-medium">清理超时未付订单</h2>
            <p className="mt-1 text-xs text-ink-500">
              把过了付款时限还没付的订单收掉，<strong>库存和优惠券都还回去</strong>。
              <br />
              ⚠️ 不清理的话，下单不付的库存会被永久占死 —— 真想买的人反而显示无货。
              上线后应该挂成定时任务，别只靠这里手点。
            </p>
            <Button
              size="sm"
              className="mt-3"
              loading={expireOverdue.isPending}
              onClick={() => expireOverdue.mutate()}
            >
              执行
            </Button>
            {expireOverdue.data && (
              <p className="mt-2 text-xs text-emerald-700">
                收掉了 {expireOverdue.data.expired} 单
              </p>
            )}
            {expireOverdue.error && <ErrorBlock error={expireOverdue.error} />}
          </section>

          <section className="rounded-lg border border-neutral-200 bg-white p-5">
            <h2 className="text-sm font-medium">释放到期分账</h2>
            <p className="mt-1 text-xs text-ink-500">
              把已过售后期（确认收货 + 15 天）的分账转成"可结算"。钱还没进余额。
            </p>
            <Button size="sm" className="mt-3" loading={releaseDue.isPending} onClick={() => releaseDue.mutate()}>
              执行
            </Button>
            {releaseDue.data && (
              <p className="mt-2 text-xs text-emerald-700">释放了 {releaseDue.data.released} 条</p>
            )}
          </section>

          <section className="rounded-lg border border-neutral-200 bg-white p-5">
            <h2 className="text-sm font-medium">结算入账</h2>
            <p className="mt-1 text-xs text-ink-500">
              给所有有可结算分账的商家结一遍，钱进余额。
              <br />
              ⚠️ 正负一起结：退款扣回也在这一步冲抵，商家余额可能因此变负。
            </p>
            <Button size="sm" className="mt-3" loading={runSettlement.isPending} onClick={() => runSettlement.mutate()}>
              执行
            </Button>
            {runSettlement.data && (
              <p className="mt-2 text-xs text-emerald-700">
                扫了 {runSettlement.data.total} 个商家，结算 {runSettlement.data.settled} 个
              </p>
            )}
          </section>
          {(releaseDue.error || runSettlement.error) && (
            <ErrorBlock error={releaseDue.error ?? runSettlement.error} />
          )}
        </div>
        </>
      )}

      {tab === "tax" && (
        <>
        <div>
          {/*
            🔴 这段不是客套话：代扣是法定义务，但口径错了是税务问题。
               默认关着，要财务确认后在配置中心打开。
          */}

          <h2 className="mb-2 text-sm font-medium">按所属期汇总</h2>
          {taxSummary.isPending ? (
            <Spinner compact />
          ) : (taxSummary.data?.length ?? 0) === 0 ? (
            <Empty title="还没有代扣记录" hint="商家提现时如果需要代扣个税，会在这里留一条台账" compact />
          ) : (
            <table className="mb-6 w-full text-sm">
              <thead className="text-left text-xs text-ink-500">
                <tr>
                  <th className="py-2">所属期</th>
                  <th>人数</th>
                  <th>收入额合计</th>
                  <th>代扣税额</th>
                  <th>待申报</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {taxSummary.data!.map((r) => (
                  <tr key={r.period} className="border-t border-neutral-100">
                    <td className="py-2.5 font-mono">{r.period}</td>
                    <td>{r.people}</td>
                    <td>
                      <Price cents={r.grossAmount} />
                    </td>
                    <td className="text-brand-600">
                      <Price cents={r.taxAmount} />
                    </td>
                    <td>{r.pending}</td>
                    <td className="space-x-3 text-right">
                      {/*
                        ⚠️ 导出带证件号原文，是个人信息出口 —— 服务端每次都会记一条日志。
                      */}
                      <a
                        className="text-xs text-brand-600"
                        href={`/api/admin/tax/export/${r.period}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        导出申报明细
                      </a>
                      {r.pending > 0 && (
                        <button
                          className="inline-flex h-7 items-center rounded-md border px-2.5 text-[12px] whitespace-nowrap transition-colors disabled:opacity-40 border-border text-muted-foreground hover:bg-secondary hover:text-foreground"
                          onClick={() => fileTax.mutate(r.period)}
                          disabled={fileTax.isPending}
                        >
                          标记已申报
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h2 className="mb-2 text-sm font-medium">代扣明细</h2>
          {taxLedger.isPending ? (
            <Spinner compact />
          ) : (taxLedger.data?.length ?? 0) === 0 ? (
            <Empty title="还没有代扣记录" hint="商家提现时如果需要代扣个税，会在这里留一条台账" compact />
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-ink-500">
                <tr>
                  <th className="py-2">纳税人</th>
                  <th>证件号</th>
                  <th>方式</th>
                  <th>收入额</th>
                  <th>减除</th>
                  <th>应纳税所得</th>
                  <th>税率</th>
                  <th>代扣</th>
                  <th>所属期</th>
                  <th>状态</th>
                </tr>
              </thead>
              <tbody>
                {taxLedger.data!.map((r) => (
                  <tr key={r.id} className="border-t border-neutral-100">
                    <td className="py-2.5">{r.taxpayerName}</td>
                    {/* 🔴 列表里一律脱敏，原文只在导出时出现 */}
                    <td className="font-mono text-xs">{r.taxpayerIdNoMasked}</td>
                    <td className="text-xs">
                      {r.method === "labor" ? "劳务报酬" : r.method === "flat" ? "核定" : "—"}
                    </td>
                    <td>
                      <Price cents={r.grossAmount} />
                    </td>
                    <td className="text-xs text-ink-500">
                      <Price cents={r.deduction} />
                    </td>
                    <td className="text-xs">
                      <Price cents={r.taxableAmount} />
                    </td>
                    <td className="text-xs">{(r.rate * 100).toFixed(0)}%</td>
                    <td className="text-brand-600">
                      <Price cents={r.taxAmount} />
                    </td>
                    <td className="font-mono text-xs">{r.period}</td>
                    <td>
                      {r.status === "pending" ? (
                        <Badge tone="mute">待申报</Badge>
                      ) : (
                        <Badge tone="ok">已申报</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {fileTax.error && <ErrorBlock error={fileTax.error} />}
        </div>
        </>
      )}

      {tab === "cases" && !cases.error && (
        <>
        <div className="flex min-h-[calc(100dvh-8.5rem)] flex-col gap-4">
          {/*
            ⚠️ 页顶那块"案例只存 productIds"的说明删了 —— 后台不放页面说明
               （老板的既定要求）。那条规则仍然写在 `admin-case-editor.tsx`
               的文件头和挑商品那一格旁边，那才是会被读到的位置。
          */}
          <div className="flex items-center justify-end">
            <Button size="sm" onClick={() => setCaseEditing(null)}>
              新增案例
            </Button>
          </div>
          {cases.isPending ? (
            <Spinner compact />
          ) : cases.data.items.length === 0 ? (
            <Empty
              title="还没有装修案例"
              hint="案例把商品放进一个具体的家里，比商品列表更容易让人下决心"
              compact
              fill
              action={{ label: "新增第一篇", onClick: () => setCaseEditing(null) }}
            />
          ) : (
            <Table fill head={["标题", "slug", "风格", "引用商品", "浏览", "状态", "操作"]}>
              {cases.data.items.map((c) => (
                <tr key={c.id} className="hover:bg-neutral-50">
                  <Td>{c.title}</Td>
                  <Td className="font-mono text-xs text-ink-500 select-all">{c.slug}</Td>
                  <Td className="text-xs">{c.style || "—"}</Td>
                  <Td className="text-xs">{c.productIds.length} 个</Td>
                  <Td className="text-xs tabular-nums">{c.viewCount}</Td>
                  <Td>
                    {c.published ? <Badge tone="ok">已发布</Badge> : <Badge tone="mute">草稿</Badge>}
                  </Td>
                  <Td>
                    <div className="flex justify-end gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => setCaseEditing(c)}>
                        编辑
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={toggleCase.isPending}
                        onClick={() => toggleCase.mutate(c)}
                      >
                        {c.published ? "下线" : "发布"}
                      </Button>
                      {/*
                        ⚠️ **删除和下线是两件事。** 下线 = 前台看不到，内容还在、
                           随时能再发；删除 = 这篇没了，浏览数也一起没了。
                           只给下线的话，写废的草稿会永远堆在列表里。
                      */}
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-destructive"
                        onClick={() => setCaseToDelete(c)}
                      >
                        删除
                      </Button>
                    </div>
                  </Td>
                </tr>
              ))}
            </Table>
          )}
          {(toggleCase.error || delCase.error) && (
            <ErrorBlock error={toggleCase.error ?? delCase.error} />
          )}

          <CaseEditor
            open={caseEditing !== undefined}
            editing={caseEditing ?? null}
            onClose={() => setCaseEditing(undefined)}
          />

          <ReasonDialog
            open={Boolean(caseToDelete)}
            title="删除这篇案例"
            description="🔴 删了就没有了：正文、配图、浏览数一起没掉。只是不想让它出现在前台的话，用「下线」——内容还在，随时能再发。"
            confirmLabel="确认删除"
            danger
            loading={delCase.isPending}
            onConfirm={() => caseToDelete && delCase.mutate(caseToDelete.id)}
            onClose={() => setCaseToDelete(null)}
          />
        </div>
        </>
      )}

      {tab === "admins" && <AdminAccountsTab />}

      {tab === "roles" && <AdminRolesTab />}

      {tab === "notify" && (
        <>
        <div className="space-y-6">
          {/*
            ⚠️ 通道没配时队列里全是「已跳过」—— 一行说清就行，
               不要在这里写一段说明（页面说明整体已经删掉了）。
          */}
          {notify.data && !notify.data.configured && (
            <p className="text-[13px] text-amber-700">
              邮件通道还没配，新的通知会直接标成「已跳过」。
            </p>
          )}

          <section>
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-sm font-medium">通知队列</h2>
              <div className="flex items-center gap-2">
                {(
                  [
                    ["", "全部"],
                    ["pending", "待发"],
                    ["sent", "已发出"],
                    ["skipped", "已跳过"],
                    ["failed", "失败"],
                  ] as const
                ).map(([k, label]) => (
                  <button
                    key={k}
                    onClick={() => setNotifyStatus(k)}
                    className={`rounded-full px-2.5 py-1 text-xs ${
                      notifyStatus === k ? "bg-brand-600 text-white" : "bg-neutral-100 text-ink-500"
                    }`}
                  >
                    {label}
                    {notify.data?.counts[k] !== undefined && (
                      <span className="ml-1 opacity-70">{notify.data.counts[k]}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {notify.isPending ? (
              <Spinner compact />
            ) : (notify.data?.items.length ?? 0) === 0 ? (
              <p className="text-xs text-ink-500">这个状态下没有记录。</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-ink-500">
                  <tr>
                    <th className="py-1">时间</th>
                    <th>事件</th>
                    <th>收件人</th>
                    <th>状态</th>
                    <th>说明</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {notify.data!.items.map((n) => (
                    <tr key={n.id} className="border-t border-neutral-100 align-top">
                      <td className="py-1.5 text-xs text-ink-500">
                        {new Date(n.createdAt).toLocaleString("zh-CN")}
                      </td>
                      <td className="text-xs">
                        {n.event === "order_paid"
                          ? "支付成功"
                          : n.event === "order_shipped"
                            ? "已发货"
                            : n.event === "refund_approved"
                              ? "退款到账"
                              : n.event}
                      </td>
                      {/* ⚠️ 没邮箱的显式写出来，别留一个空格子让人猜 */}
                      <td className="text-xs">
                        {n.toEmail || <span className="text-ink-500">（用户没填邮箱）</span>}
                      </td>
                      <td className="text-xs">
                        {n.status === "sent" ? (
                          <span className="text-emerald-700">已发出</span>
                        ) : n.status === "pending" ? (
                          `待发${n.attempts > 0 ? `（重试 ${n.attempts} 次）` : ""}`
                        ) : n.status === "skipped" ? (
                          "已跳过"
                        ) : (
                          <span className="text-red-600">失败</span>
                        )}
                      </td>
                      <td className="text-xs text-ink-500">{n.lastError}</td>
                      <td>
                        {n.status !== "sent" && (
                          <button
                            className="inline-flex h-7 items-center rounded-md border px-2.5 text-[12px] whitespace-nowrap transition-colors disabled:opacity-40 border-brand-200 text-brand-700 hover:border-brand-300 hover:bg-brand-50"
                            onClick={() => retryNotify.mutate([n.id])}
                          >
                            重发
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {retryNotify.data && (
              <p className="mt-2 text-sm text-emerald-700">
                已重新排队 {retryNotify.data.requeued} 条，下一轮定时任务会发出去。
              </p>
            )}
            {retryNotify.error && <ErrorBlock error={retryNotify.error} />}
          </section>
        </div>
        </>
      )}

      {tab === "jobs" && (
        <>
        <div className="space-y-6">
          {/*
            🔴 这几件事以前全靠人在后台点。没人点的时候平台在安静地烂掉，
               而且每一样的表现都是"什么都没发生"，最难发现。
          */}
          <div className="rounded border border-neutral-200 bg-neutral-50 px-4 py-3 text-xs leading-relaxed text-ink-500">
            这几个任务现在由服务端自动跑。它们不跑的时候不会报错，只会
            <strong>什么都不发生</strong> —— 超时订单占死库存、商家的钱永远到不了
            余额、周期费白送。所以这一页要盯的是「<strong>上次跑是什么时候</strong>」，
            不是「有没有报错」。
          </div>

          <section>
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-sm font-medium">任务健康</h2>
              <Button size="sm" variant="outline" onClick={() => jobs.refetch()}>
                刷新
              </Button>
            </div>
            {jobs.isPending ? (
              <Spinner compact />
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-ink-500">
                  <tr>
                    <th className="py-1">任务</th>
                    <th>间隔</th>
                    <th>上次</th>
                    <th>结果</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {(jobs.data?.items ?? []).map((j) => (
                    <tr key={j.name} className="border-t border-neutral-100 align-top">
                      <td className="py-1.5">
                        {j.label}
                        <span className="ml-1.5 text-xs text-ink-500">{j.name}</span>
                      </td>
                      <td className="text-xs text-ink-500">
                        {j.intervalMs >= 3_600_000
                          ? `${Math.round(j.intervalMs / 3_600_000)} 小时`
                          : `${Math.round(j.intervalMs / 60_000)} 分钟`}
                      </td>
                      <td className="text-xs">
                        {j.lastAt ? (
                          <span className={j.overdue ? "text-red-600" : "text-ink-500"}>
                            {new Date(j.lastAt).toLocaleString("zh-CN")}
                            {j.lastDurationMs !== null && (
                              <span className="ml-1 opacity-70">{j.lastDurationMs}ms</span>
                            )}
                          </span>
                        ) : (
                          /* 🔴 从没跑过 —— 这正是最该被看到的那种故障 */
                          <span className="text-red-600">从没跑过</span>
                        )}
                        {j.overdue && j.lastAt && (
                          <span className="ml-1 rounded bg-red-50 px-1.5 py-0.5 text-red-700">
                            超时未跑
                          </span>
                        )}
                      </td>
                      <td className="text-xs text-ink-500">
                        {j.lastStatus === "failed" ? (
                          <span className="text-red-600">失败：{j.lastError}</span>
                        ) : j.lastResult ? (
                          Object.entries(j.lastResult)
                            .map(([k, v]) => `${k}=${v}`)
                            .join(" · ") || "无事可做"
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        <button
                          className="inline-flex h-7 items-center rounded-md border px-2.5 text-[12px] whitespace-nowrap transition-colors disabled:opacity-40 border-brand-200 text-brand-700 hover:border-brand-300 hover:bg-brand-50"
                          onClick={() => runJob.mutate(j.name)}
                        >
                          立刻跑一次
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {runJob.data && (
              <p className="mt-2 text-sm text-emerald-700">
                {runJob.data.label}：
                {runJob.data.status === "skipped"
                  ? /* ⚠️ 没抢到锁不是失败 —— 说清楚，免得运营以为出问题了 */
                    "另一个实例正在跑，这次跳过（正常）"
                  : runJob.data.status === "failed"
                    ? `失败：${runJob.data.error}`
                    : Object.entries(runJob.data.result ?? {})
                        .map(([k, v]) => `${k}=${v}`)
                        .join(" · ") || "跑完了，没有需要处理的数据"}
              </p>
            )}
            {runJob.error && <ErrorBlock error={runJob.error} />}
          </section>

          <section>
            <h2 className="mb-2 text-sm font-medium">最近的运行记录</h2>
            {(runs.data?.items.length ?? 0) === 0 ? (
              <Empty title="还没有记录" compact />
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-ink-500">
                  <tr>
                    <th className="py-1">时间</th>
                    <th>任务</th>
                    <th>状态</th>
                    <th>耗时</th>
                    <th>结果</th>
                  </tr>
                </thead>
                <tbody>
                  {(runs.data?.items ?? []).map((r) => (
                    <tr key={r.id} className="border-t border-neutral-100">
                      <td className="py-1.5 text-xs text-ink-500">
                        {new Date(r.startedAt).toLocaleString("zh-CN")}
                      </td>
                      <td className="text-xs">{r.name}</td>
                      <td className="text-xs">
                        {r.status === "ok" ? (
                          "成功"
                        ) : r.status === "skipped" ? (
                          "跳过"
                        ) : (
                          <span className="text-red-600">失败</span>
                        )}
                      </td>
                      <td className="text-xs text-ink-500">{r.durationMs}ms</td>
                      <td className="text-xs text-ink-500">
                        {r.error ||
                          Object.entries(r.result ?? {})
                            .map(([k, v]) => `${k}=${v}`)
                            .join(" · ")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </div>
        </>
      )}

      {tab === "fees" && (
        <>
        <div className="space-y-6">
          {/*
            🔴 这段话必须摆在最上面。周期费与保证金是六种收费方式里
               合规风险最高的两种 —— 运营点这一页之前就该知道边界在哪。
          */}

          <section>
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-sm font-medium">到期扣款</h2>
              <Button
                size="sm"
                variant="outline"
                loading={runBilling.isPending}
                onClick={() => runBilling.mutate()}
              >
                扣一次到期的周期费
              </Button>
            </div>
            <p className="text-xs text-ink-500">
              重复点没关系：推进周期用的是条件更新，抢不到就跳过，不会扣两次。
              上线后这一步要挂定时任务。
            </p>
            {runBilling.data && (
              <p className="mt-2 text-sm text-emerald-700">
                扣款 {runBilling.data.charged} 笔（合计 <Price cents={runBilling.data.amount} />）
                · 余额不足转过期 {runBilling.data.expired} 个 · 不再适用而停收{" "}
                {runBilling.data.stopped} 个
              </p>
            )}
            {runBilling.error && <ErrorBlock error={runBilling.error} />}
          </section>

          <section>
            <h2 className="mb-2 text-sm font-medium">选一个节点</h2>
            <div className="flex flex-wrap gap-2">
              {(orgs.data ?? [])
                .filter((o) => o.type === "dealer" || o.type === "distributor")
                .map((o) => (
                  <button
                    key={o.id}
                    onClick={() => setFeeOrgId(o.id)}
                    className={`rounded-full px-3 py-1.5 text-xs ${
                      o.id === feeOrgId ? "bg-brand-600 text-white" : "bg-neutral-100 text-ink-500"
                    }`}
                  >
                    {o.name}
                    <span className="ml-1 opacity-70">
                      {o.type === "dealer" ? "经销商" : "分销商"}
                    </span>
                  </button>
                ))}
            </div>
          </section>

          {feeOrgId && (
            <>
              <section>
                <h2 className="mb-2 text-sm font-medium">可收的费用</h2>
                {feeOptions.isPending ? (
                  <Spinner compact />
                ) : (feeOptions.data?.items.length ?? 0) === 0 ? (
                  /* ⚠️ 分销商这里天然是空的 —— 说清楚为什么，别让运营以为是没配 */
                  <p className="text-xs text-ink-500">
                    这个节点没有可收的周期费或保证金（分销商按合规要求不收）。
                  </p>
                ) : (
                  <div className="space-y-2">
                    {feeOptions.data!.items.map((o) => (
                      <div
                        key={o.id}
                        className="flex items-center gap-3 rounded border border-neutral-200 px-3 py-2"
                      >
                        <span className="text-sm">
                          {o.kind === "periodic"
                            ? o.period === "monthly"
                              ? "月费"
                              : o.period === "quarterly"
                                ? "季费"
                                : "年费"
                            : "保证金"}
                        </span>
                        <Price cents={o.amount} />
                        <span className="flex-1 text-xs text-ink-500">{o.note}</span>
                        <Button
                          size="sm"
                          variant="outline"
                          loading={chargeFee.isPending}
                          onClick={() => chargeFee.mutate({ kind: o.kind, policyId: o.id })}
                        >
                          {o.kind === "periodic" ? "签约并扣首期" : "收取"}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                {chargeFee.error && <ErrorBlock error={chargeFee.error} />}
              </section>

              <section>
                <h2 className="mb-2 text-sm font-medium">周期费订阅</h2>
                {(feeAccount.data?.subscriptions.length ?? 0) === 0 ? (
                  <Empty title="还没有周期费订阅" hint="只对经销商开放" compact />
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs text-ink-500">
                      <tr>
                        <th className="py-1">周期</th>
                        <th>金额</th>
                        <th>状态</th>
                        <th>当前期</th>
                        <th>下次扣款</th>
                      </tr>
                    </thead>
                    <tbody>
                      {feeAccount.data!.subscriptions.map((x) => (
                        <tr key={x.id} className="border-t border-neutral-100">
                          <td className="py-1.5">
                            {x.period === "monthly" ? "月" : x.period === "quarterly" ? "季" : "年"}
                          </td>
                          <td>
                            <Price cents={x.amount} />
                          </td>
                          <td>
                            {x.status === "active"
                              ? "生效中"
                              : x.status === "expired"
                                ? "已过期（余额不足，未停售）"
                                : "已取消"}
                          </td>
                          <td className="text-xs text-ink-500">
                            {new Date(x.currentPeriodStart).toLocaleDateString("zh-CN")} ~{" "}
                            {new Date(x.currentPeriodEnd).toLocaleDateString("zh-CN")}
                          </td>
                          <td className="text-xs text-ink-500">
                            {x.nextBillAt
                              ? new Date(x.nextBillAt).toLocaleDateString("zh-CN")
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>

              <section>
                <div className="mb-2 flex items-baseline gap-2">
                  <h2 className="text-sm font-medium">保证金</h2>
                  <span className="text-xs text-ink-500">
                    在押 <Price cents={feeAccount.data?.held ?? 0} />
                  </span>
                </div>
                {/*
                  🔴 保证金不是平台收入，是代持的押金 ——
                     它不在商家余额里，也不在平台余额里。写清楚免得财务来问。
                */}
                <p className="mb-2 text-xs text-ink-500">
                  保证金交的时候就从商家余额扣走了，<strong>不进任何人的余额</strong> ——
                  它是代持的押金。只有没收之后才变成平台收入。
                </p>
                {(feeAccount.data?.deposits.length ?? 0) === 0 ? (
                  <Empty title="还没有保证金记录" compact />
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs text-ink-500">
                      <tr>
                        <th className="py-1">金额</th>
                        <th>状态</th>
                        <th>时间</th>
                        <th>操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {feeAccount.data!.deposits.map((d) => (
                        <tr key={d.id} className="border-t border-neutral-100">
                          <td className="py-1.5">
                            <Price cents={d.amount} />
                          </td>
                          <td>
                            {d.status === "held"
                              ? "在押"
                              : d.status === "refunded"
                                ? "已退还"
                                : d.status === "forfeited"
                                  ? `已没收：${d.forfeitReason}`
                                  : d.status}
                          </td>
                          <td className="text-xs text-ink-500">
                            {d.paidAt ? new Date(d.paidAt).toLocaleDateString("zh-CN") : "—"}
                          </td>
                          <td className="space-x-2">
                            {d.status === "held" && (
                              <>
                                <button
                                  className="inline-flex h-7 items-center rounded-md border px-2.5 text-[12px] whitespace-nowrap transition-colors disabled:opacity-40 border-brand-200 text-brand-700 hover:border-brand-300 hover:bg-brand-50"
                                  onClick={() => refundDeposit.mutate(d.id)}
                                >
                                  退还
                                </button>
                                <button
                                  className="inline-flex h-7 items-center rounded-md border px-2.5 text-[12px] whitespace-nowrap transition-colors disabled:opacity-40 border-destructive/25 text-destructive hover:border-destructive/40 hover:bg-destructive/5"
                                  onClick={() =>
                                    /* 🔴 没收必须写理由，前端也不放行空的 */
                                    setAsk({
                                      title: "没收这笔保证金",
                                      description:
                                        "🔴 没收之后这笔钱变成平台收入，退不回去了。纠纷时你写的这段话是唯一依据。",
                                      confirmLabel: "确认没收",
                                      danger: true,
                                      onConfirm: (reason) =>
                                        forfeitDeposit.mutate({ id: d.id, reason }),
                                    })
                                  }
                                >
                                  没收
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {(refundDeposit.error || forfeitDeposit.error) && (
                  <ErrorBlock error={refundDeposit.error ?? forfeitDeposit.error!} />
                )}
              </section>
            </>
          )}
        </div>
        </>
      )}

      {tab === "rules" && (
        <>
        <div className="space-y-8">
          <section>
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-sm font-medium">分销商等级</h2>
              <Button size="sm" variant="outline" loading={evaluate.isPending} onClick={() => evaluate.mutate()}>
                重新评级
              </Button>
            </div>
            {/*
              🔴 等级不是荣誉称号，它直接决定进货价（scope=level 的价格规则
                 比的是**买方**的等级）和佣金。改门槛会让一批人的进货价当场变化。
            */}
            <p className="mb-3 text-xs text-ink-500">
              等级决定<strong>他能拿到的进货价</strong>与佣金比例。达标后由「重新评级」升上去，
              默认<strong>只升不降</strong> —— 降级会让人手里的进货价突然变贵，是投诉源。
            </p>
            {evaluate.data && (
              <p className="mb-2 text-xs text-emerald-700">
                扫了 {evaluate.data.scanned} 个节点，调整了 {evaluate.data.changed.length} 个
                {evaluate.data.changed.length > 0 &&
                  `：${evaluate.data.changed.map((c) => c.name).join("、")}`}
              </p>
            )}
            {levels.isPending ? (
              <Spinner compact />
            ) : (levels.data?.length ?? 0) === 0 ? (
              <Empty title="还没有等级" hint="等级决定这个人的进货价和佣金比例" compact />
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-ink-500">
                  <tr>
                    <th className="py-2">等级</th>
                    <th>标识</th>
                    <th>序</th>
                    <th>累计成交门槛</th>
                    <th>直接下级门槛</th>
                    <th>状态</th>
                  </tr>
                </thead>
                <tbody>
                  {levels.data!.map((l) => (
                    <tr key={l.key} className="border-t border-neutral-100">
                      <td className="py-2.5">{l.name}</td>
                      <td className="font-mono text-xs text-ink-500">{l.key}</td>
                      <td className="text-xs">{l.rank}</td>
                      <td>
                        <Price cents={l.thresholdGmv} />
                      </td>
                      <td className="text-xs">{l.thresholdDirectCount} 个</td>
                      <td>
                        {l.active ? <Badge tone="ok">启用</Badge> : <Badge tone="mute">停用</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section>
            <h2 className="mb-2 text-sm font-medium">抽成策略</h2>
            {/*
              🔴 这些规则直接决定每一笔分账怎么抽 —— 开关一动，
                 下一单的钱就按新规则分了。合规红线也在这里。
            */}
            {policies.isPending ? (
              <Spinner compact />
            ) : (policies.data?.length ?? 0) === 0 ? (
              <Empty title="还没有抽成策略" hint="当前是纯差价模式：每一级赚的就是自己的差价" compact />
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-ink-500">
                  <tr>
                    <th className="py-2">类型</th>
                    <th>作用范围</th>
                    <th>数值</th>
                    <th>适用对象</th>
                    <th>保底线</th>
                    <th>状态</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {policies.data!.map((p) => (
                    <tr key={p.id} className="border-t border-neutral-100">
                      <td className="py-2.5">{FEE_KIND_LABEL[p.kind] ?? p.kind}</td>
                      <td className="text-xs">
                        {p.scope}
                        {p.scopeValue ? `:${p.scopeValue}` : ""}
                      </td>
                      <td>
                        {/* per_order 是固定金额（分），其余是比例 */}
                        {p.kind === "per_order" ? (
                          <Price cents={Number(p.value)} />
                        ) : (
                          `${(Number(p.value) * 100).toFixed(2)}%`
                        )}
                      </td>
                      <td className="text-xs">{(p.applicableOrgTypes ?? []).join(" / ") || "—"}</td>
                      <td className="text-xs">{(Number(p.minRetainRate) * 100).toFixed(0)}%</td>
                      <td>
                        {p.enabled ? <Badge tone="ok">生效中</Badge> : <Badge tone="mute">未启用</Badge>}
                      </td>
                      <td className="text-right">
                        <button
                          className="inline-flex h-7 items-center rounded-md border px-2.5 text-[12px] whitespace-nowrap transition-colors disabled:opacity-40 border-brand-200 text-brand-700 hover:border-brand-300 hover:bg-brand-50"
                          onClick={() => togglePolicy.mutate(p)}
                          disabled={togglePolicy.isPending}
                        >
                          {p.enabled ? "停用" : "启用"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {(togglePolicy.error || evaluate.error) && (
              <ErrorBlock error={togglePolicy.error ?? evaluate.error} />
            )}
          </section>
        </div>
        </>
      )}

      {tab === "appearance" && <AppearanceTab />}

      {tab === "login-page" && <LoginPageTab />}

      {tab === "spaces" && <SpacesTab />}

      {tab === "settings" && !settings.error && (
        <>
          {settings.isPending ? (
            <Spinner compact />
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {Object.entries(settings.data).map(([group, rec]) => (
                <section key={group} className="rounded-lg border border-neutral-200 bg-white p-5">
                  <div className="mb-3 flex items-center justify-between">
                    <h2 className="text-sm font-medium">{GROUP_LABEL[group] ?? group}</h2>
                    <div className="flex items-center gap-2">
                      {rec.isDefault && <Badge tone="mute">默认值</Badge>}
                      {editingGroup !== group && (
                        <button
                          className="inline-flex h-7 items-center rounded-md border px-2.5 text-[12px] whitespace-nowrap transition-colors disabled:opacity-40 border-brand-200 text-brand-700 hover:border-brand-300 hover:bg-brand-50"
                          onClick={() => setEditingGroup(group)}
                        >
                          编辑
                        </button>
                      )}
                    </div>
                  </div>

                  {editingGroup === group ? (
                    <SettingsForm
                      group={group}
                      rec={rec}
                      onDone={() => {
                        setEditingGroup(null);
                        void qc.invalidateQueries({ queryKey: ["admin", "settings"] });
                      }}
                    />
                  ) : (
                  <dl className="space-y-1.5 text-xs">
                    {Object.entries(rec.value).map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-4">
                        <dt className="text-ink-500">{FIELD_LABEL[k] ?? k}</dt>
                        <dd className="text-right">
                          {(() => {
                            /* ⚠️ 枚举显示中文说明，不是原始值 —— offline_direct 对老板没有意义。 */
                            const opt = FIELD_OPTIONS[k]?.find(([val]) => val === v);
                            if (opt) return opt[1];
                            if (v === null) return "不限";
                            if (typeof v === "boolean") return v ? "是" : "否";
                            return <span className="font-mono">{String(v)}</span>;
                          })()}
                        </dd>
                      </div>
                    ))}
                  </dl>
                  )}
                  {rec.updatedBy && (
                    <p className="mt-3 text-xs text-ink-500">
                      最后由 {rec.updatedBy} 于 {new Date(rec.updatedAt).toLocaleString("zh-CN")} 修改
                    </p>
                  )}
                </section>
              ))}
            </div>
          )}
          <p className="mt-4 max-w-4xl text-xs text-ink-500">
            这些开关改完立即生效，不用重新部署。保存时带乐观锁 ——
            两个人同时编辑同一组，后存的会被挡下而不是静默覆盖前一个人的改动。
          </p>
        </>
      )}

      {/*
        ⚠️ 两个对话框挂在最外层渲染一次 —— 每一行各挂一个的话，
           一张五十行的表就会挂五十个 Portal。
      */}
      <ReasonDialog
        open={Boolean(ask)}
        title={ask?.title ?? ""}
        description={ask?.description}
        confirmLabel={ask?.confirmLabel}
        danger={ask?.danger}
        onConfirm={(reason) => {
          ask?.onConfirm(reason);
          setAsk(null);
        }}
        onClose={() => setAsk(null)}
      />
      <PickDialog
        open={Boolean(pick)}
        title={pick?.title ?? ""}
        description={pick?.description}
        label={pick?.label ?? ""}
        options={pick?.options ?? []}
        confirmLabel={pick?.confirmLabel}
        onConfirm={(value, reason) => {
          pick?.onConfirm(value, reason);
          setPick(null);
        }}
        onClose={() => setPick(null)}
      />
    </Desk>
  );
}

const FEE_KIND_LABEL: Record<string, string> = {
  margin: "差价",
  periodic: "周期费",
  gmv_commission: "按成交额抽成",
  profit_commission: "按利润抽成",
  per_order: "按单收费",
  deposit: "保证金",
};

interface AdminLevel {
  key: string;
  name: string;
  rank: number;
  thresholdGmv: number;
  thresholdDirectCount: number;
  active: boolean;
}

interface AdminFeePolicy {
  id: number;
  kind: string;
  scope: string;
  scopeValue: string;
  enabled: boolean;
  value: string;
  applicableOrgTypes: string[];
  minRetainRate: string;
}

interface AdminOrder {
  id: number;
  orderNo: string;
  shipName: string;
  shipPhone: string;
  payableAmount: number;
  refundedAmount: number;
  payment: string;
  fulfillment: string;
  createdAt: string;
  /** ⚠️ 快照字段一律带 `Snapshot` 后缀 —— 写成 `title` 会拿到 undefined。 */
  items: { id: number; titleSnapshot: string; specsSnapshot: Record<string, string> }[];
}

interface AdminRefund {
  id: number;
  refundNo: string;
  orderId: number;
  orderNo: string;
  paidAmount: number;
  alreadyRefunded: number;
  amount: number;
  reason: string;
}

interface AdminUser {
  userId: number;
  nickname: string;
  phone: string | null;
  boundOrgId: number | null;
  boundOrgName: string | null;
  boundAt: string | null;
  expiresAt: string | null;
}

interface BindingLog {
  id: number;
  fromOrgId: number | null;
  toOrgId: number | null;
  reason: string;
  operator: string;
  createdAt: string;
}

interface AuditResult {
  txnCount: number;
  txnSum: number;
  accountBalance: number;
  matched: boolean;
  chain: { ok: boolean };
}

interface TaxSummary {
  period: string;
  people: number;
  grossAmount: number;
  taxAmount: number;
  pending: number;
}

interface TaxLedgerRow {
  id: number;
  taxpayerName: string;
  /** ⚠️ 服务端已脱敏，前端拿不到原文。 */
  taxpayerIdNoMasked: string;
  method: string;
  grossAmount: number;
  deduction: number;
  taxableAmount: number;
  rate: number;
  taxAmount: number;
  period: string;
  status: string;
}


export const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  component: Admin,
  validateSearch: (s: Record<string, unknown>): { tab?: string } => ({
    tab: typeof s.tab === "string" ? s.tab : undefined,
  }),
});
